// Publication-ready deep dives for the six flagship marketing routes.
//
// This is deliberately separate from the 115-route catalogue: only workflows with enough
// repository evidence to support the full marketing schema live here. Every measurable outcome is
// framed as a metric a school can track, never a promised result. Provider, device, plan and release
// dependencies stay in the data so the visual component cannot accidentally hide them.

const metric = (label, formula, interpretation) => ({ label, formula, interpretation });
const stage = (label, detail, control, evidence, kind = "record") => ({
  label,
  detail,
  control,
  evidence,
  kind,
});

export const DEEP_DIVE_MODULES = Object.freeze({
  "fee-collection": {
    title: "Smart Fees, Receivables & Governed Recovery",
    tagline: "Every rupee. One provable trail.",
    pitch:
      "Build fee structures, raise demands, collect at the counter or online, and keep one invoice-led account of what was billed, paid, adjusted and still due. Gateway evidence can update an invoice; final bank credit, provider fees and deductions remain separate reconciliation evidence for the school.",
    workflows: [
      {
        title: "Exact-paise fee structures",
        description:
          "Define class-wise heads in paise with HSN/SAC, GST rate and monthly, quarterly, term or annual frequency.",
      },
      {
        title: "Controlled demand generation",
        description:
          "Raise a class or period in bulk through an idempotent path that skips an invoice already generated for the same demand.",
      },
      {
        title: "One invoice state",
        description:
          "Review due, part-paid, paid, overdue and terminal invoices without maintaining a parallel collection spreadsheet.",
      },
      {
        title: "Counter and online collection",
        description:
          "Record authorised cash, UPI, cheque and card evidence, or open an invoice-bound order through the school's configured provider.",
      },
      {
        title: "Invoice-bound payment links",
        description:
          "Offer no-login Pay Now links or QR payments while the server clamps the amount to the invoice's current outstanding due.",
      },
      {
        title: "Partial payment and advance balance",
        description:
          "Accept a permitted partial payment and keep eligible excess evidence as a student advance that can be applied later.",
      },
      {
        title: "Governed relief and refunds",
        description:
          "Route concessions, scholarships and refunds through their implemented permission and second-approver controls instead of quietly editing a due amount.",
      },
      {
        title: "Previewed late-fee rules",
        description:
          "Preview the affected invoices before posting a configured late-fee rule, then retain the resulting line-item evidence.",
      },
      {
        title: "Sequential receipts",
        description:
          "Mint a sequential receipt only for recorded payment evidence and retain the invoice relationship for later review.",
      },
      {
        title: "Separate settlement reconciliation",
        description:
          "Keep provider callbacks, receipts and invoice state distinct from the final bank statement matching that confirms credit and deductions.",
      },
    ],
    automation: {
      label: "Aira and supervised recovery",
      title: "Prioritise follow-up without giving AI authority over money.",
      summary:
        "Aira can prepare reviewable wording, while recovery eligibility, base scoring, stage selection and invoice suppression come from bounded product rules. Enabled recovery uses approved templates and rechecks the current invoice before dispatch.",
      actions: [
        "Prepare a single-invoice reminder draft for fee-desk editing; the draft is not persisted or sent by that request.",
        "Prioritise eligible overdue invoices from deterministic collection signals, with any optional model adjustment kept bounded.",
        "Run an explicitly enabled reminder ladder through configured templates, channels, consent, opt-out, quiet-mode and cutoff controls.",
        "Remove confirmed paid, voided, cancelled or zero-due invoices from an active recovery path when the current state is rechecked.",
        "Create a final human call task for the office; the system does not place the call.",
        "Retain enablement, cancellation, run, stage and stop-reason evidence for authorised review.",
      ],
      boundary:
        "Aira cannot waive, refund, write off, change an invoice, autonomously send generated free-form recovery copy, or prove final settlement. An authorised person reviews material changes and the school reconciles provider or bank evidence.",
    },
    uses: [
      "Generate the next billing period and inspect skipped or exceptional invoices.",
      "Collect at the fee desk and issue a receipt against the invoice evidence.",
      "Review overdue exposure by class, ageing and payment state.",
      "Approve or reject a concession, scholarship application or refund through its supported control.",
      "Supervise an enabled recovery cohort and complete its final human call tasks.",
    ],
    roles: [
      "Accountant",
      "Fee-desk staff",
      "Principal",
      "Tenant administrator",
      "Authorised finance approver",
      "Parent or guardian",
    ],
    metrics: [
      metric(
        "Confirmed collection rate",
        "Provider- or counter-confirmed collection ÷ billed amount",
        "Separate recorded collection from an initiated checkout or an unverified promise to pay.",
      ),
      metric(
        "Overdue exposure",
        "Open overdue invoice count and value, segmented by ageing",
        "Shows where receivables need review without claiming that a reminder caused payment.",
      ),
      metric(
        "Reconciliation quality",
        "Matched, ambiguous and unmatched bank-statement entries",
        "Keeps final cash evidence visible instead of treating a provider callback as bank settlement.",
      ),
    ],
    availability: {
      status: "Core collection · configured provider for online payments · recovery needs Automation included on your plan",
      prerequisites: [
        "Define the fee structure, billing period, due dates and authorised finance roles before generating demands.",
        "Online collection requires the school's own configured Razorpay or Cashfree account and verified callback setup.",
        "Recovery needs a plan that includes Automation, plus an explicitly enabled policy, approved templates and configured communication channels.",
        "Bank credit, provider fees and deductions still require provider or bank reconciliation evidence.",
        "Payroll is a separate workspace that is still rolling out school by school and is not part of this fees module.",
      ],
    },
    visual: {
      title: "The money-evidence timeline",
      description:
        "Select a stage to see what changes, where a person remains accountable, and which evidence the workflow retains.",
      stages: [
        stage(
          "Invoice issued",
          "A demand is generated from the configured fee structure in exact paise.",
          "Duplicate-period protection and authorised generation guard the write.",
          "Invoice lines, due date, fee structure and generation identity.",
          "record",
        ),
        stage(
          "Payment initiated",
          "The fee desk records a supported method or the parent opens an invoice-bound provider order.",
          "The server clamps the amount to the current due and applies idempotency controls.",
          "Method, provider order or counter reference and current outstanding amount.",
          "provider",
        ),
        stage(
          "Evidence confirmed",
          "Counter evidence or a verified provider callback updates the invoice payment record.",
          "A closed browser tab or unverified client response cannot mark the invoice paid.",
          "Payment, callback disposition and invoice reconciliation history.",
          "review",
        ),
        stage(
          "Receipt issued",
          "A sequential receipt is linked to the confirmed payment evidence.",
          "Authorised staff can retrieve the issued artifact without rewriting the underlying invoice.",
          "Receipt number, payment link and issue context.",
          "evidence",
        ),
        stage(
          "Bank reconciled",
          "The school compares settlement evidence with its statement and ledger account.",
          "Ambiguous and unmatched entries stay visible for finance review.",
          "Saved reconciliation run with matched, ambiguous and unmatched states.",
          "evidence",
        ),
      ],
    },
  },

  "enquiry-management": {
    title: "Admissions Pipeline & Aira-Assisted Follow-up",
    tagline: "Every enquiry gets an owner and a next step.",
    pitch:
      "Bring public enquiries, counsellor follow-ups, applicant registration, documents and final enrolment into one governed pipeline. Teams can see where a family is waiting, while admission decisions and student creation remain authorised human actions.",
    workflows: [
      {
        title: "Six-stage pipeline",
        description:
          "Move enquiries through New, Contacted, Visit, Application, Admitted or Lost with live stage counts and funnel context.",
      },
      {
        title: "Public enquiry capture",
        description:
          "Accept school website enquiries through a rate-limited public path and flag likely duplicates for review.",
      },
      {
        title: "Source and class context",
        description:
          "Retain where an enquiry came from and the class of interest so the team can compare its funnel responsibly.",
      },
      {
        title: "Counsellor ownership",
        description:
          "Assign responsibility, record follow-up history and keep a visible next action rather than relying on a private notebook.",
      },
      {
        title: "OTP applicant registration",
        description:
          "Let an applicant submit a registration through OTP verification before staff review the supplied record.",
      },
      {
        title: "Review and correction queue",
        description:
          "Authorised staff can review, request correction, approve or reject submitted registration information.",
      },
      {
        title: "Document carry-over",
        description:
          "Keep eligible applicant photos and documents attached when an admitted lead becomes a student.",
      },
      {
        title: "Capacity-aware placement",
        description:
          "Check the configured class cap and use the least-filled-section hint as decision support, not automatic placement authority.",
      },
      {
        title: "Atomic lead conversion",
        description:
          "Create the student and guardian record with a server-minted admission number and mark the lead converted in one controlled transaction.",
      },
      {
        title: "Admissions reporting",
        description:
          "Review current source, stage and conversion counts from the recorded pipeline.",
      },
    ],
    automation: {
      label: "Aira-assisted follow-up",
      title: "Draft the next message without deciding who gets admitted.",
      summary:
        "Aira can draft a warm follow-up from bounded pipeline context such as stage, source, class of interest, time since last contact and follow-up count. Names, phone numbers and email addresses are not supplied to this drafting agent.",
      actions: [
        "Prepare a reviewable follow-up draft from the current non-identity pipeline context.",
        "Return the draft to the authorised counsellor without persisting it as completed outreach.",
        "Let the counsellor edit the wording, choose the actual channel and separately record what was sent or discussed.",
        "Keep duplicate flags, seat hints and funnel statistics as decision support for staff review.",
      ],
      boundary:
        "Aira does not send the follow-up, score or rank applicants, decide admission, assign a child automatically, or convert a lead. An authorised person reviews the draft and performs every admission-state change.",
    },
    uses: [
      "Work today's assigned follow-up queue and record the outcome.",
      "Find enquiries that have remained in one stage without a next step.",
      "Compare current applications and admissions by recorded source.",
      "Request missing or corrected applicant information.",
      "Convert a reviewed and accepted enquiry into a student record.",
    ],
    roles: [
      "Admission counsellor",
      "Admission manager",
      "Front-office executive",
      "Principal",
      "Tenant administrator",
      "Parent or applicant",
    ],
    metrics: [
      metric(
        "Pipeline conversion",
        "Admitted enquiries ÷ eligible recorded enquiries",
        "Review conversion by source and period without presenting it as an AI-generated forecast.",
      ),
      metric(
        "Follow-up discipline",
        "Open enquiries with an owner, next step and recent contact record",
        "Highlights missing operational ownership before a family drops out of the process.",
      ),
      metric(
        "Source effectiveness",
        "Applications and admissions grouped by recorded source",
        "Helps the school compare channel quality using its own funnel evidence.",
      ),
    ],
    availability: {
      status: "Admissions catalogue capability · optional Aira follow-up drafting on plans that include it",
      prerequisites: [
        "Confirm admission roles, pipeline definitions, class capacity and the school's acceptance policy.",
        "Public capture and OTP registration require their configured public and messaging paths.",
        "Aira drafting needs the Aira AI assistant included on your plan, a governance precheck and available credits.",
        "Staff must verify applicant data, documents, consent and school policy before conversion.",
      ],
    },
    visual: {
      title: "A governed admissions passage",
      description:
        "The interactive pipeline shows where information moves and where a human decision remains mandatory.",
      stages: [
        stage(
          "Enquiry captured",
          "A public, walk-in or phone enquiry enters the school pipeline.",
          "Rate limits and duplicate flags protect the intake; a flag does not delete or reject a lead.",
          "Source, class of interest, created time and duplicate-review state.",
          "record",
        ),
        stage(
          "Owner assigned",
          "A counsellor takes responsibility and records the next follow-up.",
          "Only authorised admissions staff can update the pipeline record.",
          "Owner, stage, next step and follow-up history.",
          "scope",
        ),
        stage(
          "Aira drafts",
          "Aira prepares wording from bounded pipeline context.",
          "The request does not persist or send the draft and does not receive direct contact details.",
          "Governed invocation and credit evidence where enabled.",
          "review",
        ),
        stage(
          "Counsellor follows up",
          "The counsellor edits the message and records the actual outreach separately.",
          "The person — not the model — chooses whether, when and how to contact the family.",
          "Follow-up note, channel and timestamp supplied by staff.",
          "action",
        ),
        stage(
          "Application reviewed",
          "Submitted registration details and documents enter the staff review queue.",
          "Staff can request correction, approve or reject; no model decides eligibility.",
          "Registration state, corrections and review result.",
          "review",
        ),
        stage(
          "Student created",
          "An authorised conversion creates the student and guardian record and closes the lead stage.",
          "Capacity is rechecked and the conversion runs atomically with a server-minted admission number.",
          "Student identity, guardian record, conversion audit and carried documents.",
          "evidence",
        ),
      ],
    },
  },

  "result-publishing": {
    title: "Exam Governance, CCE & Approval-Ready Report Cards",
    tagline: "From marks entry to publication — without spreadsheet drift.",
    pitch:
      "Configure terms, papers, board scales and report-card rules from the school's academic setup. Teachers enter scoped marks, moderators review exceptions, and authorised staff generate and publish report cards through distinct controlled stages.",
    workflows: [
      {
        title: "Board-resolved grading",
        description:
          "Use the configured CBSE, ICSE, state or IB scale type, grade bands and pass marks instead of page-specific hard-coding.",
      },
      {
        title: "CCE assessment context",
        description:
          "Classify supported assessments as formative, summative, project, oral or practical and review their configured roll-up.",
      },
      {
        title: "Paper and datesheet setup",
        description:
          "Create term papers by class, section and subject with maximum marks, pass marks and clash-aware dates.",
      },
      {
        title: "Teacher-scoped marks entry",
        description:
          "Limit a teacher to authorised class-section-subject assignments, clamp marks to the paper maximum and preserve absence as its own state.",
      },
      {
        title: "Completion and statistics",
        description:
          "Review entered, absent, passed, failed and aggregate paper statistics before the result deadline.",
      },
      {
        title: "Different-person moderation",
        description:
          "Route submitted marks through the implemented second-examiner path where the school uses moderation.",
      },
      {
        title: "Publish locks and reopening",
        description:
          "Lock marks when results publish and use the authorised reopening path for a correction instead of silently editing issued work.",
      },
      {
        title: "Report Card Studio",
        description:
          "Configure supported layout, signatories, grading scales and holistic domains without changing the result source.",
      },
      {
        title: "Readiness preview",
        description:
          "Run supported readiness checks before generating a class batch so incomplete records remain visible.",
      },
      {
        title: "Controlled card lifecycle",
        description:
          "Generate, review, withhold, release and deliver report-card artifacts through their authorised states.",
      },
      {
        title: "Frozen issue context",
        description:
          "Retain the issued template snapshot and version so later design changes do not rewrite an earlier card.",
      },
      {
        title: "Supported exports",
        description:
          "Use the implemented CBSE OASIS, ICSE or state-oriented output where available, and batch cards into an expiring ZIP link.",
      },
    ],
    automation: {
      label: "Aira for analysis and remarks",
      title: "Let AI draft the words — not the marks or the decision.",
      summary:
        "Aira can explain aggregate exam patterns and prepare a board-aware class-teacher remark from permitted marks, attendance and configured academic context. The returned text remains an editable draft.",
      actions: [
        "Explain supported class-level exam aggregates without receiving student names for the aggregate-analysis request.",
        "Draft a class-teacher remark only when the permitted report-card evidence contains marks.",
        "Suggest strengths and focus areas for a teacher to verify against the source record.",
        "Optionally translate a reviewed remark when the configured provider, language and your plan support it.",
        "Prepare a sanitized report-card design suggestion without altering marks or publication state.",
      ],
      boundary:
        "Aira does not assign or change marks, determine pass or fail, approve moderation, sign a card, publish results or release a withheld card. Teachers and authorised examination staff own every saved remark and publication decision.",
    },
    uses: [
      "Configure a term and publish a clash-checked examination plan.",
      "Track missing or invalid marks before the entry deadline.",
      "Moderate submitted marks through a different authorised reviewer.",
      "Preview a class's report-card readiness and resolve incomplete records.",
      "Generate, approve, withhold or release report-card artifacts.",
    ],
    roles: [
      "Subject teacher",
      "Class teacher",
      "Examination controller",
      "Academic coordinator",
      "Moderator",
      "Principal",
      "Parent or student after publication",
    ],
    metrics: [
      metric(
        "Marks completion",
        "Entered or explicitly absent cells ÷ expected mark cells",
        "Makes missing entry work visible before publication rather than hiding it in a class average.",
      ),
      metric(
        "Report-card readiness",
        "Complete, partial and missing student records before generation",
        "Shows which records need review before a batch is generated.",
      ),
      metric(
        "Publication control",
        "Pending moderation, withheld cards, reopened papers and correction events",
        "Tracks governed exceptions without treating publication speed as the only quality measure.",
      ),
    ],
    availability: {
      status: "Academic catalogue capability · board configuration required · optional Aira on plans that include it",
      prerequisites: [
        "Configure the school board, academic year, grading scale, classes, sections, subjects and teaching assignments.",
        "Create the term and papers, then complete the permitted marks and moderation workflow before publication.",
        "Board export availability depends on the selected board and the supported output for that workflow.",
        "Aira remarks need the Aira AI assistant included on your plan, available credits and a teacher review before save or publication.",
      ],
    },
    visual: {
      title: "The result-control rail",
      description:
        "Move through the evidence behind a card; the visual stops at every point where academic authority is required.",
      stages: [
        stage(
          "Configure",
          "The school resolves its board scale, term, papers, assessment types and report-card design.",
          "Board configuration is authoritative; AI does not create the grading policy.",
          "Resolved scale, paper settings, signatories and template version.",
          "record",
        ),
        stage(
          "Enter",
          "A scoped teacher records marks or an explicit absence against the expected roster.",
          "Server-side assignment checks and maximum-mark clamps protect each entry.",
          "Mark cells, absence states, remarks and entry completion.",
          "scope",
        ),
        stage(
          "Moderate",
          "A different authorised reviewer checks the submitted evidence where moderation is enabled.",
          "The review cannot be replaced by an Aira explanation or remark draft.",
          "Moderation state, reviewer and note.",
          "review",
        ),
        stage(
          "Generate",
          "The card is rendered from the authorised marks, attendance, board and studio configuration.",
          "Readiness checks surface incomplete source records before the batch proceeds.",
          "Generated card, template snapshot and version evidence.",
          "action",
        ),
        stage(
          "Publish",
          "Authorised staff publish or withhold the card and marks lock against ordinary editing.",
          "Only the supported reopen and correction path can change the governed result state.",
          "Publication, withholding, release and correction history.",
          "evidence",
        ),
      ],
    },
  },

  "biometric-attendance": {
    title: "Biometric & Device Attendance Bridge",
    tagline: "Every attendance mark keeps its source.",
    pitch:
      "Combine teacher-marked day or period registers with compatible biometric, RFID or QR punch evidence. Device punches remain subordinate to existing teacher marks, and unmatched identities stay visible for review instead of disappearing.",
    workflows: [
      {
        title: "Day and period registers",
        description:
          "Record present, absent, late or leave against the authorised class-section roster.",
      },
      {
        title: "Teacher-section boundary",
        description:
          "Limit classroom staff to the sections their current teaching assignments permit.",
      },
      {
        title: "Idempotent register save",
        description:
          "Submit an offline-tolerant register without creating a duplicate mark on a safe retry.",
      },
      {
        title: "Correction evidence",
        description:
          "Retain the before-and-after state when an authorised teacher re-marks a saved cell.",
      },
      {
        title: "Registered device identity",
        description:
          "Give each compatible device or middleware connection a tenant-scoped ingest token that can be rotated.",
      },
      {
        title: "Provider-agnostic punch bridge",
        description:
          "Accept the supported biometric, RFID or QR identity event through school-supplied compatible middleware.",
      },
      {
        title: "Conservative IN handling",
        description:
          "A matched student's IN punch may fill an empty present mark; it cannot replace an existing teacher mark.",
      },
      {
        title: "Evidence-only OUT handling",
        description:
          "Keep OUT punches in the punch log rather than using them to rewrite the classroom register.",
      },
      {
        title: "Unmatched review queue",
        description:
          "Hold an unresolved admission number or device identity for staff review instead of dropping the event.",
      },
      {
        title: "Attendance analysis",
        description:
          "Review daily, monthly, class, chronic-absence and configured threshold views from saved attendance records.",
      },
    ],
    automation: {
      label: "Bounded attendance assistance",
      title: "Explain the register; never rewrite it through AI.",
      summary:
        "Aira may explain a permitted attendance summary through the separately enabled assistant surface. Deterministic attendance rules can identify saved-register exceptions or configured shortfall cases for staff review.",
      actions: [
        "Explain a permitted attendance aggregate using the source record returned by an authorised tool.",
        "Flag chronic, at-risk or configured board-threshold cases from deterministic attendance calculations.",
        "Queue an eligible guardian alert only through the configured communication, consent, opt-out and quiet-mode path.",
        "Keep device mapping and unmatched-punch resolution with an authorised operator.",
      ],
      boundary:
        "Aira does not create, correct or approve attendance. A teacher saves the register, an operator resolves unmatched punches, and a device event never overrides an existing manual mark. Geo-fenced attendance is not part of this module.",
    },
    uses: [
      "Mark and, when authorised, correct a class or period register.",
      "Review missing registers and current attendance trends.",
      "Resolve unmatched device punches against the correct tenant identity.",
      "Rotate a compromised device ingest token.",
      "Review configured shortfall cases before any family follow-up.",
    ],
    roles: [
      "Class teacher",
      "Attendance coordinator",
      "Vice principal",
      "Principal",
      "Tenant administrator",
      "Device operator",
      "Parent or guardian as an alert recipient",
    ],
    metrics: [
      metric(
        "Attendance visibility",
        "Saved daily, rolling and class attendance rates",
        "Uses the recorded register and keeps missing-register context separate from absence.",
      ),
      metric(
        "Device matching quality",
        "Matched versus unmatched punches by registered device",
        "Shows whether identity mapping needs attention before a device bridge is trusted operationally.",
      ),
      metric(
        "Follow-up readiness",
        "Chronic, at-risk and configured threshold-shortfall case counts",
        "Prioritises human review without presenting an AI prediction as an attendance fact.",
      ),
    ],
    availability: {
      status: "Biometric attendance on a plan that includes it · compatible device middleware required",
      prerequisites: [
        "Confirm a compatible reader or school-supplied middleware that can post the supported event shape.",
        "Register each device, protect and rotate its ingest token, and map student admission numbers within the correct tenant.",
        "The generic bridge is not a bundled vendor-specific biometric SDK and does not include hardware.",
        "Staff punches need the separate HR & Payroll workspace, which is still rolling out school by school, and do not automatically become payroll attendance.",
        "Geo-fenced attendance is not included; where a supported workflow offers location-based proof, ask us to map it to your school.",
      ],
    },
    visual: {
      title: "Two sources, one governed register",
      description:
        "See how teacher intent and device evidence meet without allowing a punch to erase a human-marked state.",
      stages: [
        stage(
          "Roster resolved",
          "The current class-section roster and teacher scope establish who can be marked.",
          "A device identity is never allowed to widen the teacher's record access.",
          "Enrolment-backed roster and teaching assignment.",
          "scope",
        ),
        stage(
          "Teacher register",
          "The teacher records the classroom state by day or period.",
          "A correction retains before-and-after evidence instead of silently replacing history.",
          "Manual source, actor, saved state and correction diff.",
          "record",
        ),
        stage(
          "Device punch",
          "Compatible middleware posts a tenant-scoped identity, direction and event to the registered bridge.",
          "The per-device token can be rotated and the event is rate-limited and tenant-scoped.",
          "Device, event direction, identity and received time.",
          "provider",
        ),
        stage(
          "Reconcile source",
          "A matched IN event may fill only an empty present mark; OUT and unmatched events remain evidence.",
          "An existing teacher mark wins and an unmatched identity enters review.",
          "Matched, unmatched, retained-manual or evidence-only disposition.",
          "review",
        ),
        stage(
          "Review outcome",
          "Staff inspect the register, punch queue and eligible follow-up views.",
          "Aira can explain permitted aggregates but cannot write or approve a mark.",
          "Saved register, exception queue and configured alert evidence.",
          "evidence",
        ),
      ],
    },
  },

  "parent-aira": {
    title: "Parent Communications & Own-Child Aira",
    tagline: "The right message. The right family. The right scope.",
    pitch:
      "Plan school communication through configured WhatsApp, SMS, email, in-app and early-access push channels. Parent Aira is a separate read-only experience that answers supported questions about the authenticated family's own children through enabled web, early-access mobile, voice and opt-in WhatsApp paths.",
    workflows: [
      {
        title: "Scoped audience selection",
        description:
          "Choose an authorised stakeholder, class or section audience and preview the eligible recipient count before publish.",
      },
      {
        title: "Masked recipient preview",
        description:
          "Review representative masked contact evidence without exposing a full family contact list in the composer.",
      },
      {
        title: "Overlapping-audience deduplication",
        description:
          "Resolve overlapping audience tokens to one recipient identity per student or staff record so the same selected identity is not fanned out twice.",
      },
      {
        title: "Configured channel templates",
        description:
          "Use Meta-approved WhatsApp and DLT-registered SMS templates with the school's provider credentials.",
      },
      {
        title: "Email and in-app delivery",
        description:
          "Route email through the configured sender and publish supported notices to the signed-in portal experience.",
      },
      {
        title: "Early-access push",
        description:
          "Use supported push notifications and deep links only inside the enabled early-access app programme.",
      },
      {
        title: "Schedule and expiry",
        description:
          "Choose a future publication time and an expiry while keeping the authored message available for staff review.",
      },
      {
        title: "Consent and quiet-mode controls",
        description:
          "Apply channel consent, opt-out, provider and onboarding quiet-mode checks before an eligible send.",
      },
      {
        title: "Provider-reported evidence",
        description:
          "Review attempted, sent, delivered, read or failed states only where the configured provider returns them.",
      },
      {
        title: "Own-child Aira",
        description:
          "Answer supported questions about the signed-in guardian's linked children across fees, attendance, published marks and transport data.",
      },
    ],
    automation: {
      label: "Aira with a family boundary",
      title: "Draft for staff. Read for parents. Never widen the child scope.",
      summary:
        "Aira can prepare communication copy for an authorised staff member to review. Parent Aira separately uses read-only, permission-checked tools that resolve only student IDs linked to the authenticated guardian.",
      actions: [
        "Draft a school notice or reminder for staff editing before the communication publish path.",
        "Answer supported own-child fee, attendance, published-mark and transport questions from authorised current records.",
        "Use the same child linkage on web, early-access mobile, configured voice and opt-in WhatsApp access paths.",
        "Check that the plan includes the Aira AI assistant, then apply the governance precheck, configured identifier safeguards and credit budget before an AI request.",
        "Refuse child-data answers for an unknown WhatsApp sender until the required identity and opt-in relationship is established.",
      ],
      boundary:
        "Parent Aira is read-only and cannot update a school record, send a school broadcast or access another family's child. Staff review generated outbound copy; provider acceptance does not prove delivery or reading.",
    },
    uses: [
      "Preview and schedule a class or whole-school circular.",
      "Draft a multilingual notice for authorised staff review.",
      "Review failed or ambiguous provider evidence before choosing whether to retry.",
      "Let a verified guardian ask about their own child's fees, attendance or published result.",
      "Review reachability, opt-outs and quiet-mode skips before a campaign.",
    ],
    roles: [
      "Communications operator",
      "Principal",
      "Vice principal",
      "Admission manager",
      "Tenant administrator",
      "Teacher in an authorised communication scope",
      "Parent or guardian",
    ],
    metrics: [
      metric(
        "Reachability",
        "Eligible recipients with a valid, consented configured channel",
        "Separates audience size from the families a provider can actually attempt to reach.",
      ),
      metric(
        "Provider-reported delivery",
        "Attempted, sent, delivered, read and failed states by channel",
        "Uses each provider's returned evidence and avoids treating acceptance as guaranteed reading.",
      ),
      metric(
        "Governance disposition",
        "Opted-out, quiet-mode-skipped, failed and manual-review message states",
        "Makes policy and delivery exceptions visible instead of hiding them inside a single delivery percentage.",
      ),
    ],
    availability: {
      status: "Aira AI assistant on a plan that includes it · configured channels · native apps in early access",
      prerequisites: [
        "Parent access requires an authenticated guardian account linked only to that family's student records.",
        "WhatsApp and SMS require school-owned provider credentials, approved templates, consent and applicable Meta or DLT rules.",
        "Email requires configured delivery; provider acceptance does not guarantee inbox placement.",
        "Native mobile and push remain early access ahead of public app-store release.",
        "Voice and opt-in WhatsApp Aira require their configured providers, identity path, assistant credits and channel enablement.",
      ],
    },
    visual: {
      title: "The family-scope communication path",
      description:
        "Switch stages to see the difference between an outbound school message and a read-only parent question.",
      stages: [
        stage(
          "Audience preview",
          "Authorised staff select the school audience and inspect eligible recipient evidence.",
          "Masked samples, deduplication, consent and role scope are visible before publish.",
          "Selected audience, recipient count and exclusion reasons.",
          "scope",
        ),
        stage(
          "Copy reviewed",
          "Staff author the message or edit an optional Aira draft.",
          "Generated wording has no independent publish authority.",
          "Reviewed title, body, channel and schedule.",
          "review",
        ),
        stage(
          "Policy checked",
          "The communication path applies template, consent, opt-out, quiet-mode and provider rules.",
          "A recipient that fails an eligibility check is skipped with a reason.",
          "Eligibility and skip disposition.",
          "review",
        ),
        stage(
          "Provider attempted",
          "The configured provider receives the eligible message attempt.",
          "A provider-accepted state is not presented as proof of delivery or reading.",
          "Attempt, provider reference and callback evidence where supported.",
          "provider",
        ),
        stage(
          "Parent asks",
          "A verified guardian asks a supported question in an enabled Parent Aira channel.",
          "The tool resolves only children linked to the authenticated family and remains read-only.",
          "Child-scope check, permitted source and governed AI usage evidence.",
          "scope",
        ),
        stage(
          "Scoped answer",
          "Aira explains the authorised source record to the parent.",
          "The answer cannot mutate the record or reach another family's child.",
          "Read-only response context and source authority.",
          "evidence",
        ),
      ],
    },
  },

  "branch-management": {
    title: "Multi-Campus Governance & Group Oversight",
    tagline: "One group view. Every campus stays bounded.",
    pitch:
      "Give a trust or school chain a legal-entity-scoped view of member schools, student counts, fee exposure, plan state and group operations. Central oversight stays read-oriented; campus writes continue through each school's own roles, permissions and operating controls.",
    workflows: [
      {
        title: "Member-school roster",
        description:
          "Review the schools attached to the legal entity with each campus's status, plan and academic context.",
      },
      {
        title: "Per-campus KPIs",
        description:
          "Compare current student count, each school's Vidyapeeth360 subscription cost and open fee-outstanding evidence by member school.",
      },
      {
        title: "Group roll-up",
        description:
          "Aggregate schools, active schools, students, Vidyapeeth360 subscription cost, outstanding fees and plan mix inside the caller's group scope.",
      },
      {
        title: "Request-time comparison",
        description:
          "Compute the supported comparison from each member tenant context instead of persisting a marketing estimate.",
      },
      {
        title: "Consolidated subscription statement",
        description:
          "Generate one monthly legal-entity statement with per-school lines in exact paise and a CSV export.",
      },
      {
        title: "Frozen final statements",
        description:
          "Prevent a paid or cancelled group statement from being silently regenerated with different point-in-time values.",
      },
      {
        title: "Consent-gated student transfer",
        description:
          "Open an intra-group transfer only after guardian consent is acknowledged, then require an authorised approve or reject decision.",
      },
      {
        title: "Shared identity continuity",
        description:
          "Carry the shared person identity and recorded APAAR or UDISE values into the target enrolment while closing the source after approval.",
      },
      {
        title: "Group brand inheritance",
        description:
          "Apply the legal entity's brand defaults to member schools while respecting an authorised campus override.",
      },
      {
        title: "Paused SOP pack rollout",
        description:
          "Install a versioned operations pack across member schools; every campus copy starts paused for local role and timing review.",
      },
      {
        title: "Compliance roll-up",
        description:
          "Compare current due, due-soon, in-progress and overdue compliance-calendar counts by member school.",
      },
      {
        title: "Failure-isolated rollout",
        description:
          "Retain a per-campus success or failure result so one school's transient error does not silently conceal the rest of the group operation.",
      },
    ],
    automation: {
      label: "Deterministic group governance",
      title: "Centralise oversight without inventing a group-wide AI authority.",
      summary:
        "The group console uses legal-entity scope, deterministic roll-ups and explicit transfer or rollout decisions. Aira may remain available inside separately authorised campus workflows, but it does not gain trust-wide access from this module.",
      actions: [
        "Calculate group summaries and campus comparisons from member-school records inside the caller's legal-entity boundary.",
        "Require guardian consent before a transfer request and a separate authorised approval before the target record is created.",
        "Install group SOP templates in a paused state so each campus can review roles, times and local operating fit.",
        "Keep customised campus SOP items from being silently replaced during a later group resync.",
        "Return per-campus rollout results and current compliance-calendar counts for human follow-up.",
      ],
      boundary:
        "There is no group-wide Aira command surface in this module. Group oversight does not grant unrestricted campus writes, and Aira on one school's plan does not widen its role or record scope to the trust.",
    },
    uses: [
      "Compare current student and fee-outstanding evidence across member schools.",
      "Generate or export the group's monthly subscription statement.",
      "Review due-soon and overdue compliance items by campus.",
      "Install a school-operations SOP pack for campus-level review.",
      "Process a guardian-consent-backed inter-school transfer.",
    ],
    roles: [
      "Trustee",
      "Group administrator",
      "Central operations lead",
      "Group finance leader",
      "Campus principal",
      "School tenant administrator",
      "Authorised transfer reviewer",
    ],
    metrics: [
      metric(
        "Group coverage",
        "Member schools, active schools and current student count",
        "Shows the current operating footprint inside the legal entity's authorised scope.",
      ),
      metric(
        "Operational exposure",
        "Open fee-outstanding value plus due-soon and overdue compliance counts by campus",
        "Makes campus-level attention areas visible without turning them into an AI risk score.",
      ),
      metric(
        "Governed rollout",
        "Pending or completed transfers and SOP rollout success or failure by school",
        "Tracks whether a central action reached each campus and where a human needs to intervene.",
      ),
    ],
    availability: {
      status: "Group & Chain on a plan that includes it · confirmed legal-entity and member-school model",
      prerequisites: [
        "Confirm the legal entity, member tenants, group roles and campus-specific role boundaries before enabling the console.",
        "Cross-campus writes still require the relevant group action and each workflow's own validation; oversight is otherwise read-oriented.",
        "Student transfers require acknowledged guardian consent, source and target membership, and an authorised review decision.",
        "SOP templates arrive paused; each campus must review local ownership, timing, holidays and proof requirements before activation.",
        "Aira remains tenant- and role-scoped; no group-wide Aira capability is implied.",
      ],
    },
    visual: {
      title: "The bounded campus network",
      description:
        "Follow one group operation from legal-entity scope to a campus outcome without collapsing tenant boundaries.",
      stages: [
        stage(
          "Group resolved",
          "The request resolves the caller's legal entity and its authorised member-school list.",
          "A school outside that group cannot be read or selected as an operation target.",
          "Legal entity, group role and member tenant IDs.",
          "scope",
        ),
        stage(
          "Campus evidence read",
          "Supported KPIs or compliance items are read inside each member school's own tenant context.",
          "The central console remains read-oriented for normal campus operations.",
          "Per-school source counts and request-time group roll-up.",
          "record",
        ),
        stage(
          "Decision staged",
          "An authorised user prepares a statement, transfer or paused SOP-pack rollout.",
          "Consent, membership, status and permission checks remain specific to the chosen operation.",
          "Preview, target schools and required approval context.",
          "review",
        ),
        stage(
          "Campus action",
          "The approved operation runs separately inside each affected tenant boundary.",
          "A transfer closes the source only after approval; an SOP installs paused; a final statement stays frozen.",
          "Per-campus result, actor and operation-specific record.",
          "action",
        ),
        stage(
          "Group evidence returned",
          "The console reports success, failure, pending review or completion by school.",
          "One campus failure does not become a false success or silently cancel evidence from the others.",
          "Group summary, transfer state, statement or rollout disposition.",
          "evidence",
        ),
      ],
    },
  },
  // ------------------------------------------------------------------------------------------
  // SEO-6 (2026-08-18): the top commercial-intent routes deepened to the flagship standard.
  // Every claim below is grounded in shipped, documented behaviour (CLAUDE.md SIS/attendance/
  // communication sections + the claims registry PS1/PS6/I2 wording). No invented numbers.
  // ------------------------------------------------------------------------------------------
  "student-admission": {
    title: "Student Records, Admission Numbers & Family Self-Service",
    tagline: "One record per child. One numbering scheme. One audited trail.",
    pitch:
      "Create and maintain the student register a school runs on: server-assigned admission numbers, capacity-aware section placement, guardian records, one-time credential handover, OTP-verified self-registration with a staff review queue, and family correction requests that change the record through an audited path instead of a phone call.",
    workflows: [
      {
        title: "Server-assigned admission numbers",
        description:
          "Admission numbers come from one per-school sequential allocator — never typed by hand, never duplicated across two competing schemes.",
      },
      {
        title: "Every create path shares the scheme",
        description:
          "Manual add, approved self-registration, admissions-lead conversion and reviewed bulk import all mint from the same server allocator.",
      },
      {
        title: "Capacity-aware section placement",
        description:
          "A configured class cap refuses over-admission with an explicit administrator override; a least-filled-section hint supports the decision.",
      },
      {
        title: "Guardian records and sibling visibility",
        description:
          "Guardians are recorded with the student, and siblings surface automatically from a shared guardian contact on the student's page.",
      },
      {
        title: "One-time credential handover",
        description:
          "An admin-added student receives a registration number and a generated temporary password shown once — credentials never travel over SMS.",
      },
      {
        title: "OTP-verified self-registration",
        description:
          "A public wizard verifies the applicant's mobile by OTP before the submission lands in the school's review queue as a pending request.",
      },
      {
        title: "Review, correction and approval queue",
        description:
          "Authorised staff approve, reject or request a field-scoped correction; approval creates the student and assigns the numbers.",
      },
      {
        title: "Family correction requests",
        description:
          "Students and parents request changes to whitelisted fields from their portal; the school approves and the record changes with an audit trail.",
      },
      {
        title: "Printable ID cards with public verification",
        description:
          "Generate printable student ID cards whose QR resolves to a public verification page exposing only the printed fields.",
      },
      {
        title: "Governed status changes",
        description:
          "Student status moves through a governed state machine — a withdrawal can automatically issue the transfer certificate on the way out.",
      },
      {
        title: "RTE and CWSN recording",
        description:
          "Record RTE admissions and CWSN support needs on the student record, with a dedicated report for the school's returns.",
      },
    ],
    automation: {
      label: "Migration Copilot import",
      title: "Bring your existing register across — reviewed, never blind.",
      summary:
        "The Migration Copilot reads an uploaded student CSV or XLSX, proposes a column mapping and flags row-level validation issues. Staff review and correct the staged records before anything commits.",
      actions: [
        "Propose a column mapping for the uploaded spreadsheet and flag per-row validation problems for review.",
        "Stage the records so the school can correct rows before any of them become real students.",
        "Commit the approved rows in one transaction with an audit trail, stamping each record as import-sourced.",
        "Keep imported records under onboarding safeguards so a bulk import can never trigger reminder messages by itself.",
      ],
      boundary:
        "The Copilot never admits a student on its own. A school administrator reviews the proposed mapping and the staged rows, and only their approval commits; admission numbers are always minted by the server allocator — never by the model.",
    },
    uses: [
      "Add a walk-in admission with the number, section and credentials issued in one pass.",
      "Work the self-registration review queue and request corrections where details are incomplete.",
      "Approve or reject family correction requests against the record's audit trail.",
      "Print ID cards for a class and verify one from its QR code.",
      "Run the RTE/CWSN report for the school's returns.",
    ],
    roles: [
      "Admission manager",
      "Front-office executive",
      "Class teacher",
      "Principal",
      "Tenant administrator",
      "Parent or student (self-service)",
    ],
    metrics: [
      metric(
        "Record completeness",
        "Active students with guardian contact, class-section and required documents on file ÷ active students",
        "Shows where record follow-up is needed before report-card and returns season.",
      ),
      metric(
        "Activation coverage",
        "Student and parent logins activated ÷ active students",
        "The activation report breaks this down per class-section so the school knows who still needs a credential handover.",
      ),
      metric(
        "Correction turnaround",
        "Correction requests resolved ÷ correction requests filed",
        "Keeps the self-service loop honest — a stuck queue sends families back to phone calls.",
      ),
    ],
    availability: {
      status: "Core student-record capability · self-registration and import paths are configured per school",
      prerequisites: [
        "Confirm class-section structure, capacity caps and the admission-number prefix before the first intake.",
        "Self-registration requires the school's registration window to be open and a configured messaging path for OTP delivery.",
        "The Migration Copilot import is included from the plan tier that carries migration; confirm inclusion in the live plan matrix.",
        "The school verifies applicant data, documents, consent and its own admission policy before approving any record.",
      ],
    },
    visual: {
      title: "How a student record earns its number",
      description:
        "Each stage shows what enters the record, which control gates it, and the evidence left behind.",
      stages: [
        stage(
          "Record enters",
          "A manual add, an approved self-registration, a converted admissions lead or a reviewed import row.",
          "Every path runs the same validation and the same server allocator — no side doors.",
          "Source stamp, created-by and creation time on the record.",
          "record",
        ),
        stage(
          "Number minted",
          "The per-school sequential allocator assigns the admission number.",
          "Numbers are server-assigned and unique per school; staff cannot hand-type a competing scheme.",
          "The admission number and its allocator sequence.",
          "evidence",
        ),
        stage(
          "Section placed",
          "The student lands in a class-section within the configured capacity.",
          "A full section refuses the placement unless an administrator explicitly overrides; the least-filled hint is decision support only.",
          "Class-section, capacity state and any recorded override.",
          "scope",
        ),
        stage(
          "Credentials handed over",
          "The school hands the family its registration number and one-time temporary password.",
          "The password is shown once to the issuing staff member and must be changed on first sign-in.",
          "Activation state on the activation report.",
          "action",
        ),
        stage(
          "Corrections reviewed",
          "The family requests changes to whitelisted fields from its portal.",
          "The school approves or rejects each field-scoped request; nothing changes the record silently.",
          "The correction request, decision and resulting audit entry.",
          "review",
        ),
      ],
    },
  },
  "student-attendance": {
    title: "Daily & Period Attendance with Honest Follow-Through",
    tagline: "Marked by the right teacher. Reviewed before it becomes a message.",
    pitch:
      "Run the daily roll call and period-wise registers with record-level teacher scoping, review the month at a glance, let configured scans surface low-attendance patterns, and keep every parent-facing message inside the school's communication settings. Every number is computed from the stored register — nothing is inferred.",
    workflows: [
      {
        title: "Daily roll call by class-section",
        description:
          "A teacher marks the day's register for their own class-section from the roster, with corrections recorded rather than overwritten silently.",
      },
      {
        title: "Period-wise registers",
        description:
          "Where the school configures periods, per-period attendance runs alongside the daily register without double-counting a student's day.",
      },
      {
        title: "Record-level teacher scoping",
        description:
          "A classroom teacher can mark and read only the class-sections they actually teach — resolved from the timetable and explicit teaching assignments.",
      },
      {
        title: "Homeroom coverage without a period",
        description:
          "A class-teacher assignment covers daily attendance for the homeroom even when no timetabled period exists for that teacher.",
      },
      {
        title: "Monthly grid review",
        description:
          "The month grid shows each student's marked days in one view, so a wrongly marked day is caught before it reaches a report card.",
      },
      {
        title: "Configured low-attendance scan",
        description:
          "A scheduled scan flags students falling under the school's configured attendance threshold for staff review and follow-up.",
      },
      {
        title: "Monthly parent digest",
        description:
          "Where the school enables it, a monthly attendance summary goes to parents through the configured communication channels.",
      },
      {
        title: "Optional device-fed presence",
        description:
          "A supported biometric device can feed presence events into the register; the device scope is reviewed for each deployment.",
      },
      {
        title: "Attendance on the report card",
        description:
          "Attendance context prints on report cards against board-configured thresholds, so the term's evidence matches the daily register.",
      },
      {
        title: "Attendance reports and exports",
        description:
          "The reports catalogue includes attendance registers and summaries with CSV or XLSX export for the school's own analysis.",
      },
      {
        title: "Staff attendance kept separate",
        description:
          "Staff presence runs on its own register with its own controls — student and staff attendance never share one book.",
      },
    ],
    automation: {
      label: "Deterministic signals, not model guesses",
      title: "The register speaks for itself — on a schedule.",
      summary:
        "Attendance intelligence here is deliberately deterministic: scheduled scans and digests read the stored register and the school's configured thresholds. No model marks, predicts or messages anyone.",
      actions: [
        "Flag configured low-attendance patterns from the recorded register on a schedule for staff review.",
        "Feed the deterministic at-risk view that combines attendance with other recorded factors for authorised staff.",
        "Send the configured monthly attendance digest to parents where the school has enabled it.",
        "Compute every figure from stored register rows so a number can always be traced to marked days.",
      ],
      boundary:
        "No model marks or edits attendance. Registers are recorded by authorised staff or a configured device feed; scans and digests only read what the register already says, and a flagged student is a review signal for staff — never an automatic action against a family.",
    },
    uses: [
      "Take the morning register for your own class-section in under a minute of taps.",
      "Correct a wrongly marked day with the change recorded, not overwritten.",
      "Review the month grid before a parent-teacher meeting.",
      "Work the flagged low-attendance list and record the follow-up taken.",
      "Export the register for the school's own analysis or an inspection.",
    ],
    roles: [
      "Class teacher",
      "Subject teacher",
      "Principal",
      "Vice principal",
      "Tenant administrator",
      "Parent (digest recipient)",
    ],
    metrics: [
      metric(
        "Register completion",
        "Class-sections with today's register marked ÷ total class-sections",
        "The daily discipline everything else in this module depends on.",
      ),
      metric(
        "Attendance rate",
        "Days present ÷ working days, per student or per section",
        "Reviewed by section and period band from the stored register — never presented as a prediction.",
      ),
      metric(
        "Follow-up coverage",
        "Flagged low-attendance students with a recorded staff follow-up ÷ flagged students",
        "A flag without a follow-up is just a report; this keeps the loop closed.",
      ),
    ],
    availability: {
      status: "Core capability in every plan · device feeds, scans and digests are configured per school",
      prerequisites: [
        "Class-sections and teaching assignments must reflect who actually teaches where — marking scope follows them.",
        "Period-wise registers need the school's period structure configured first.",
        "The parent digest and low-attendance scan are enabled per school and respect its communication settings and recorded opt-outs.",
        "A biometric device feed has its own device and connectivity scope, reviewed before rollout.",
      ],
    },
    visual: {
      title: "From a marked register to term evidence",
      description:
        "Each stage shows who acts, which boundary applies, and what the register can prove afterwards.",
      stages: [
        stage(
          "Register marked",
          "The class teacher marks the day — or a configured device feeds presence events.",
          "Only staff scoped to the class-section can mark it; corrections are recorded changes.",
          "Marked-by, time and the per-day register rows.",
          "record",
        ),
        stage(
          "Scope enforced",
          "The system resolves who may touch which section's register.",
          "Record-level teacher scoping from the timetable, teaching assignments and homeroom links.",
          "The teacher's resolved class-section access set.",
          "scope",
        ),
        stage(
          "Patterns scanned",
          "The scheduled scan reads the register against the configured threshold.",
          "Deterministic thresholds — a flag is a staff review signal, not a verdict on a child.",
          "The flagged list with each student's computed rate.",
          "review",
        ),
        stage(
          "Family informed",
          "The monthly digest goes out where the school enabled it.",
          "Runs through the school's configured channels, honouring opt-outs and onboarding quiet mode.",
          "Per-channel delivery outcomes in the communication log.",
          "action",
        ),
        stage(
          "Term evidence",
          "Attendance context prints on the report card.",
          "Board-configured thresholds decide how attendance is presented on the card.",
          "The printed attendance summary tied to the same register.",
          "evidence",
        ),
      ],
    },
  },
  announcements: {
    title: "Announcements & Multi-Channel Family Broadcasts",
    tagline: "One circular. Every channel. Honest delivery evidence.",
    pitch:
      "Compose one announcement and reach families across WhatsApp, SMS, email and the in-app portals in a single pass — with a live recipient count before sending, school-approved WhatsApp templates, recorded opt-outs, and a unified delivery log that reports only what each channel's provider actually returns.",
    workflows: [
      {
        title: "Audience-token broadcast builder",
        description:
          "Address parents, students, staff, teachers or one class-section with audience tokens, deduplicated so nobody is messaged twice for one circular.",
      },
      {
        title: "Live recipient preview",
        description:
          "A dry-run resolves the real recipient count before sending, so an empty class token or missing numbers surface while it is still a draft.",
      },
      {
        title: "Multi-channel fanout",
        description:
          "One publish fans out across the selected channels — WhatsApp, SMS, email and in-app — instead of four separate sending tools.",
      },
      {
        title: "Approved-template WhatsApp",
        description:
          "Business-initiated WhatsApp goes out as the school-approved template you configure. Without one, WhatsApp itself only delivers free text inside its 24-hour service window.",
      },
      {
        title: "Scheduled publishing",
        description:
          "Schedule an announcement and the worker sends it at the chosen time with the same template and audience resolution as an immediate send.",
      },
      {
        title: "Unified delivery log",
        description:
          "Email and messaging outcomes land in one delivery view, so a circular's evidence is reviewed in one place rather than per provider.",
      },
      {
        title: "Per-channel evidence honesty",
        description:
          "Review the evidence each configured channel actually returns — recorded in-app state, WhatsApp delivery or read where available, SMS acceptance only.",
      },
      {
        title: "Opt-outs recorded and honoured",
        description:
          "Inbound STOP and START messages update the recipient's preference, and opted-out numbers are skipped on the next broadcast automatically.",
      },
      {
        title: "Onboarding quiet mode",
        description:
          "A new workspace holds non-essential outbound communication until the school explicitly goes live, so setup experiments never reach families.",
      },
      {
        title: "In-app noticeboard and detail pages",
        description:
          "Announcements appear in the parent and student portals with their own detail view, independent of any external channel's delivery.",
      },
    ],
    automation: {
      label: "Drafting help, human sending",
      title: "Aira can draft the words. A person always presses send.",
      summary:
        "Where the plan includes it, Aira drafts announcement and campaign copy on request through the governed AI path. The broadcast itself — audience, channels, timing — remains a deliberate human action with the recipient count on screen.",
      actions: [
        "Draft announcement or campaign copy on request through the governed Aira path where the plan includes it.",
        "Show the honest resolved recipient count before anything can be sent.",
        "Route WhatsApp through the school-approved template you configure, so a business-initiated broadcast goes out as that template.",
        "Record per-channel outcomes into the unified delivery log for staff review.",
      ],
      boundary:
        "Aira never sends a broadcast. A person composes or accepts the copy, sees the recipient count, and presses send; channel evidence differs by provider — SMS never claims read evidence, and email opens appear only when the configured provider supplies them.",
    },
    uses: [
      "Publish a school-wide circular across WhatsApp, email and the portals in one pass.",
      "Target one class-section about tomorrow's schedule change.",
      "Schedule the exam-week notice for Monday morning.",
      "Review which channel actually accepted, delivered or recorded each message.",
      "Check that opted-out families were skipped, with the preference on record.",
    ],
    roles: [
      "Principal",
      "Vice principal",
      "Front-office executive",
      "Class teacher",
      "Tenant administrator",
      "Parents and students (recipients)",
    ],
    metrics: [
      metric(
        "Reach before send",
        "Resolved recipients in the dry-run preview ÷ intended audience size",
        "Catches an empty class token or missing contact numbers while the circular is still a draft.",
      ),
      metric(
        "Channel acceptance",
        "Provider-accepted sends ÷ attempted sends, per channel",
        "Provider acceptance is not inbox delivery — treat it as the channel's own evidence, reviewed per provider.",
      ),
      metric(
        "Opt-out rate",
        "Opt-outs recorded ÷ recipients messaged over the period",
        "A rising rate is a content-and-cadence signal the school should act on, not a metric to suppress.",
      ),
    ],
    availability: {
      status: "Core communication capability · WhatsApp, SMS and email run on the school's configured providers and approved templates",
      prerequisites: [
        "WhatsApp and SMS require provider onboarding and school-approved templates; template and registration approvals are channel-specific steps.",
        "Email runs on SendGrid or the school's own SMTP configuration.",
        "Guardian contact data and consent records must be current — the school owns both.",
        "A new workspace holds non-essential outbound communication until the school's explicit go-live.",
      ],
    },
    visual: {
      title: "One circular's honest journey",
      description:
        "Each stage shows the control that gates the broadcast and the evidence the school can point to afterwards.",
      stages: [
        stage(
          "Composed",
          "Authorised staff write the circular and choose audience tokens and channels.",
          "Only staff with communication rights compose; the audience is a defined token set, not a pasted number list.",
          "The draft, its audience definition and author.",
          "record",
        ),
        stage(
          "Previewed",
          "The dry-run resolves the audience into a real recipient count.",
          "The live count lets staff catch an empty audience or missing contacts before sending.",
          "The resolved recipient count at preview time.",
          "review",
        ),
        stage(
          "Sent per channel",
          "The fanout dispatches each selected channel in one pass.",
          "WhatsApp leaves only as an approved template; opted-out recipients are skipped automatically.",
          "Per-recipient, per-channel dispatch records.",
          "action",
        ),
        stage(
          "Evidence returns",
          "Each provider reports what it truly knows — acceptance, delivery, or read where supported.",
          "The log never upgrades a channel's evidence: SMS shows acceptance, not reads.",
          "The unified delivery dashboard entries.",
          "evidence",
        ),
        stage(
          "Read in-app",
          "The announcement also lives on the family portals with its own detail page.",
          "Portal visibility is scoped to the intended audience, independent of external channels.",
          "The in-app notification and announcement record.",
          "record",
        ),
      ],
    },
  },
  "timetable-management": {
    title: "Timetable, Workload & Substitute Coverage",
    tagline: "One grid the whole school can trust.",
    pitch:
      "Build each class-section's weekly grid with teacher double-booking detection, workload caps and a dry-run preview before anything saves, or auto-generate a draft with no clashes in what it places, listing any subject demand it could not fit. A substitute board fills the periods that approved leave leaves open, and every teacher can subscribe to their own schedule feed.",
    workflows: [
      {
        title: "Period framework first",
        description:
          "Configure working days, daily period slots and break placement once, so every class-section grid is built on the same academic week.",
      },
      {
        title: "Class-section grids",
        description:
          "Place a subject and a teacher in each period of a class-section's week, and keep the whole grid reviewable in one screen instead of a printed sheet.",
      },
      {
        title: "Double-booking detection",
        description:
          "Catch a teacher placed in two class-sections in the same period before the grid saves, rather than discovering the clash on the first day of the term.",
      },
      {
        title: "Weekly workload caps",
        description:
          "Warn when a teacher's placed periods cross the weekly cap the school configured, so load is balanced by evidence and not by memory.",
      },
      {
        title: "Dry-run preview",
        description:
          "Review the conflicts, cap warnings and unresolved placements a proposed change would produce, and decide before anything is committed to the live grid.",
      },
      {
        title: "Constraint-based draft generation",
        description:
          "Generate a draft with no clashes in what it places, and see exactly which subject demands it could not fit, so a coordinator resolves the gaps before publishing.",
      },
      {
        title: "Room and resource limits",
        description:
          "Keep a room from being scheduled twice in the same period, so a lab or hall constraint is enforced by the grid rather than by a coordinator's notes.",
      },
      {
        title: "Substitute board for approved leave",
        description:
          "See exactly which periods an approved leave leaves uncovered, on the day it matters, instead of reconstructing the gap from a leave register.",
      },
      {
        title: "Ranked cover suggestions",
        description:
          "Rank available teachers for an open period with same-subject teachers first, so a coordinator chooses from a shortlist and records who actually covered.",
      },
      {
        title: "Personal schedule feed",
        description:
          "Give each teacher a calendar feed of their own weekly schedule that they can subscribe to from Google, Apple or Outlook calendars.",
      },
      {
        title: "Timetable as teaching scope",
        description:
          "Use the same placements to establish which class-sections a teacher may reach in attendance and marks, so scheduling and record-level access never disagree.",
      },
    ],
    automation: {
      label: "Deterministic scheduling controls",
      title: "Constraints produce the draft; a person publishes the timetable.",
      summary:
        "Generation and clash detection are rule-based calculations over the school's own configuration. They surface conflicts, caps and gaps for review; they never decide the academic week on their own.",
      actions: [
        "Detect teacher double-bookings and room collisions from the configured grid before a save is accepted.",
        "Warn on weekly workload caps using the school's own per-teacher configuration.",
        "Generate a draft with no clashes in what it places, and list the subject demands it could not fit, for a coordinator to resolve.",
        "List periods left open by approved leave and rank eligible cover teachers, same subject first.",
      ],
      boundary:
        "No model writes your timetable. The generator proposes a draft from the constraints you configure, and a timetable administrator reviews and publishes it. Substitute cover is assigned by a person, from ranked suggestions, and the assignment is recorded.",
    },
    uses: [
      "Build or amend a class-section's weekly grid and review the dry-run before saving.",
      "Check a teacher's weekly load against the configured cap before adding a period.",
      "Generate a starting draft for a new term from the subject demands.",
      "Fill the periods an approved leave left open and record who covered them.",
      "Share each teacher's schedule as a calendar feed instead of a printed slip.",
    ],
    roles: [
      "Timetable administrator",
      "Academic coordinator",
      "Vice principal",
      "Principal",
      "Class teacher",
      "Subject teacher",
      "Substitute coordinator",
    ],
    metrics: [
      metric(
        "Conflict clearance",
        "Detected double-bookings and room collisions resolved before publish",
        "Shows whether the grid is being corrected at design time instead of during the teaching week.",
      ),
      metric(
        "Teaching-load balance",
        "Weekly placed periods per teacher against the configured cap",
        "Makes an over-loaded or under-used teaching allocation visible while it can still be changed.",
      ),
      metric(
        "Cover completeness",
        "Periods opened by approved leave against periods filled from the substitute board",
        "Separates a genuine staffing gap from an administrative one that was simply never assigned.",
      ),
    ],
    availability: {
      status: "Academic catalogue capability · class, subject and teacher configuration required",
      prerequisites: [
        "Classes, sections, subjects, teachers, working days and period slots configured for the academic year.",
        "Per-teacher weekly caps and any room constraints entered, or generation has nothing to honour.",
        "Leave requests routed through the leave workflow, since the substitute board reads approved leave.",
        "Calendar feeds are a subscription for each teacher's own schedule, not a two-way calendar sync.",
        "Auto-generation produces a reviewable draft for a coordinator; it is not an unattended publisher.",
      ],
    },
    visual: {
      title: "From constraints to a published week",
      description:
        "Follow one period from the configuration that bounds it to the cover that keeps it taught.",
      stages: [
        stage(
          "Framework configured",
          "Working days, period slots, subjects, teachers and caps establish the academic week.",
          "Every later check reads this one configuration, so the rules cannot fork per coordinator.",
          "Period framework, subject list and per-teacher caps.",
          "scope",
        ),
        stage(
          "Placement proposed",
          "A coordinator places a subject and teacher in a period, or generates a draft from demands.",
          "The proposal is evaluated before it is stored; nothing enters the live grid unchecked.",
          "Proposed placement and its source.",
          "action",
        ),
        stage(
          "Conflicts surfaced",
          "Double-bookings, room collisions and weekly cap breaches are reported in a dry run.",
          "A clash is shown at design time rather than discovered in the teaching week.",
          "Conflict and cap-warning list for the proposed change.",
          "review",
        ),
        stage(
          "Grid published",
          "The reviewed grid becomes the live schedule for the class-section.",
          "The published placements also define which sections a teacher may reach in records.",
          "Saved timetable entries and the resulting teaching scope.",
          "record",
        ),
        stage(
          "Cover recorded",
          "Approved leave opens periods; a ranked board proposes cover and a person assigns it.",
          "The assignment is a recorded human decision, not an automatic swap.",
          "Open-period list, chosen cover teacher and assignment record.",
          "evidence",
        ),
      ],
    },
  },

  payroll: {
    title: "Payroll, Statutory Withholding & Disbursal",
    tagline: "Run it once. Prove every deduction.",
    pitch:
      "Run monthly payroll with configured section 192 TDS, EPF and ESI handling, generate ECR and ESIC filing outputs and payslip PDFs, then disburse through bank batches that need a second approver and capture per-employee payment evidence. Paid salary results post to the school's books as gross cost with the withheld statutory amount recognised as a liability.",
    workflows: [
      {
        title: "Salary inputs per employee",
        description:
          "Hold the employee's pay components and statutory identifiers in the staff record, so a run reads one reviewed source rather than a spreadsheet re-keyed each month.",
      },
      {
        title: "Monthly run generation",
        description:
          "Generate the month's salary register for the selected employees, with each employee's earnings, deductions and net pay calculated from the configured components.",
      },
      {
        title: "Section 192 TDS with true-up",
        description:
          "Withhold monthly income tax under the configured regime and spread the projected annual liability across the financial-year months that remain, net of tax already withheld.",
      },
      {
        title: "EPF and ESI participation",
        description:
          "Apply EPF and ESI to eligible wages, or opt the school out where the Acts do not apply, and keep that choice recorded on the run itself.",
      },
      {
        title: "Locked run outputs",
        description:
          "Lock the reviewed run so the payslips, statutory outputs and ledger postings that follow all describe the same figures.",
      },
      {
        title: "Payslip PDFs",
        description:
          "Publish each employee's payslip as a branded PDF that the employee can open from their own profile without asking the payroll desk.",
      },
      {
        title: "EPFO ECR and ESIC outputs",
        description:
          "Produce the EPFO ECR text file and the ESIC contribution file from the locked run, so the filing you upload ties back to the salary register that produced it.",
      },
      {
        title: "Form-16 and 24Q worksheets",
        description:
          "Derive the annual TDS worksheets from the tax actually withheld in locked runs, rather than from an assumed annual salary figure.",
      },
      {
        title: "Approved bank disbursal",
        description:
          "Send the run to a payout batch that a second authorised person approves before any money moves to a verified employee bank destination.",
      },
      {
        title: "Settlement evidence",
        description:
          "Capture the bank reference for each employee's transfer as it settles, and mark the run paid only when its disbursed items have settled.",
      },
      {
        title: "Books that reflect the truth",
        description:
          "Post salary cost at gross with net paid from the bank and the withheld PF, ESI and tax recognised as a statutory liability instead of disappearing.",
      },
    ],
    automation: {
      label: "Calculated payroll, approved by people",
      title: "The arithmetic is deterministic; the money needs two people.",
      summary:
        "Statutory calculations run from configured rules and the school's own inputs. Nothing about a payroll run is a model output, and no calculation releases money on its own.",
      actions: [
        "Calculate monthly TDS, EPF and ESI from configured rules, wage ceilings and the school's participation choice.",
        "Spread the projected annual tax across the remaining financial-year months, net of tax already withheld.",
        "Generate ECR, ESIC, Form-16 and 24Q outputs from the locked run rather than from a re-entered figure.",
        "Post the resulting salary journal once, idempotently, whether the run was marked paid manually or settled through a payout batch.",
      ],
      boundary:
        "Payroll never pays itself. The person who creates a run cannot also mark it paid, a bank disbursal batch needs a separate approver, and an employee bank destination must be verified before it can receive salary. Statutory filing on the government portal remains a school action.",
    },
    uses: [
      "Generate and review the month's salary register before anything is locked.",
      "Confirm the statutory participation and deductions applied to this run.",
      "Send the approved run for bank disbursal and track each transfer to settlement.",
      "Download the ECR and ESIC outputs for the month's filing.",
      "Answer an employee's payslip or annual-TDS question from their own self-service record.",
    ],
    roles: [
      "Payroll officer",
      "HR administrator",
      "Finance approver",
      "Accountant",
      "Principal",
      "Employee",
      "Statutory reviewer",
    ],
    metrics: [
      metric(
        "Run completeness",
        "Employees included in the locked run against active payroll-eligible employees",
        "Shows whether anyone was silently left out before the run becomes the month's record.",
      ),
      metric(
        "Statutory withholding",
        "PF, ESI and TDS withheld in the run against the statutory liability recognised in the books",
        "Ties the salary register to the balance sheet, so a filing figure and a ledger figure cannot drift.",
      ),
      metric(
        "Disbursal settlement",
        "Approved payout items settled with a captured bank reference against items dispatched",
        "Separates a payment that is still in flight from one the bank has actually completed.",
      ),
    ],
    availability: {
      status: "HR & Payroll on a plan that includes it · still rolling out school by school",
      prerequisites: [
        "The HR & Payroll workspace enabled for your school, which is still rolling out school by school.",
        "Complete employee statutory inputs — PAN, UAN, ESIC and bank details — or the outputs run on gaps.",
        "A confirmed EPF and ESI participation decision for the school, since both can be opted out where the Acts do not apply.",
        "A configured payout gateway and verified employee bank destinations for bank disbursal.",
        "Filing on the EPFO, ESIC and TRACES portals stays a school action; the platform produces the outputs.",
      ],
    },
    visual: {
      title: "From salary inputs to settled, posted pay",
      description:
        "Follow one month from calculation to the bank reference and the ledger entry it creates.",
      stages: [
        stage(
          "Inputs reviewed",
          "Employee pay components, statutory identifiers and participation settings are confirmed.",
          "Payroll reads reviewed employee records; bank values stay in protected storage.",
          "Employee salary inputs and statutory identifiers.",
          "scope",
        ),
        stage(
          "Run calculated",
          "The month's register is generated with earnings, statutory deductions and net pay per employee.",
          "Every deduction is a deterministic calculation over configured rules, never an estimate.",
          "Salary register with per-employee earnings and deductions.",
          "record",
        ),
        stage(
          "Run locked",
          "The reviewed register is locked so payslips, filings and postings share one set of figures.",
          "A locked run is the single source for every downstream output.",
          "Locked run, payslip PDFs and statutory outputs.",
          "review",
        ),
        stage(
          "Disbursal approved",
          "The run is sent to a bank payout batch that a second authorised person approves.",
          "The run's creator cannot approve its own disbursal, and destinations must be verified.",
          "Payout batch, approver and per-employee destination.",
          "review",
        ),
        stage(
          "Settled and posted",
          "Each transfer settles with a bank reference and the salary journal posts to the books.",
          "The run is marked paid from settlement evidence, and the posting is idempotent.",
          "Bank references, paid run state and the salary journal.",
          "evidence",
        ),
      ],
    },
  },

  "staff-management": {
    title: "Staff Directory, Identity & Access",
    tagline: "One employee record. One controlled login.",
    pitch:
      "Maintain a staff directory with departments, designations, generated employee codes and server-side format checks for India statutory fields such as PAN, UAN, ESIC and IFSC. App access is granted from the same record, bank values use protected storage with masked display, and staff with linked history are deactivated rather than deleted.",
    workflows: [
      {
        title: "Directory with real structure",
        description:
          "Keep employees under departments and designations with search and filters, so a staff list stays usable as the school grows past a printed register.",
      },
      {
        title: "Generated employee codes",
        description:
          "Assign a sequential employee code from the school's own counter, so two administrators adding staff at once cannot produce the same code.",
      },
      {
        title: "Validated statutory fields",
        description:
          "Check PAN, twelve-digit UAN, ESIC and IFSC formats on the server as they are entered, instead of discovering a malformed value during a filing run.",
      },
      {
        title: "Protected finance details",
        description:
          "Store bank values in protected storage and show them masked, so a payroll destination is not casually readable from a directory screen.",
      },
      {
        title: "Employee 360 record",
        description:
          "Open one employee to see their profile, documents, service history and linked workspace records without moving between disconnected screens.",
      },
      {
        title: "Granted app access",
        description:
          "Turn a staff record into a real login in one reviewed action, which links the employee to that login and hands over credentials the person then changes.",
      },
      {
        title: "Role-based permissions",
        description:
          "Give each login only the modules its role needs, so an HR clerk maintaining the directory does not thereby acquire payroll or finance rights.",
      },
      {
        title: "Document shelf per employee",
        description:
          "Hold contracts, qualification proofs and identity documents against the employee record, with access following the module the document belongs to.",
      },
      {
        title: "Staff self-service",
        description:
          "Let a staff member open their own profile, payslips and statutory self-completion, which keeps the HR desk out of routine copy requests.",
      },
      {
        title: "Deactivate rather than delete",
        description:
          "Retire a leaver by deactivating the record so payroll, attendance and document history stay intact and auditable after they have left.",
      },
    ],
    automation: {
      label: "Governed staff records",
      title: "Access is granted deliberately, and never as a side effect.",
      summary:
        "Creating a staff record and creating a login are separate, recorded decisions. Directory work never widens someone's access on its own.",
      actions: [
        "Allocate employee codes from one per-school sequential counter that concurrent adds cannot collide on.",
        "Validate PAN, UAN, ESIC and IFSC formats server-side before the value is stored.",
        "Mint a login from an employee record only through the reviewed grant-access action, and link the two identities.",
        "Deliver handover credentials over the school's configured email and messaging channels, subject to their configuration.",
      ],
      boundary:
        "A directory entry is not a login. Access is granted by an authorised administrator in a separate, audited action, the temporary credential must be changed by its owner, and an employee whose history is linked elsewhere is deactivated rather than deleted.",
    },
    uses: [
      "Add a joiner with their department, designation and statutory identifiers.",
      "Grant, review or withdraw a staff member's app access.",
      "Open an Employee 360 record before a review, transfer or exit conversation.",
      "Complete or correct statutory fields ahead of a payroll or filing cycle.",
      "Deactivate a leaver while preserving their payroll and attendance history.",
    ],
    roles: [
      "HR administrator",
      "School administrator",
      "Principal",
      "Department head",
      "Payroll officer",
      "Employee",
    ],
    metrics: [
      metric(
        "Statutory completeness",
        "Active employees with valid PAN, UAN, ESIC and bank details against all active employees",
        "Predicts whether the next payroll and filing cycle will run on complete data or on gaps.",
      ),
      metric(
        "Access coverage",
        "Active employees with a linked login against those whose role needs one",
        "Shows who still cannot reach the workspace their job assumes they can use.",
      ),
      metric(
        "Directory hygiene",
        "Records deactivated on exit against recorded leavers",
        "Catches ex-staff who still hold an active record or an active login.",
      ),
    ],
    availability: {
      status: "HR & Payroll on a plan that includes it · still rolling out school by school",
      prerequisites: [
        "The HR & Payroll workspace enabled for your school, which is still rolling out school by school.",
        "Departments, designations and the role model your school actually uses, agreed before bulk entry.",
        "An authorised administrator to grant app access, since staff cannot self-register a login.",
        "Configured email and messaging channels if credential handover should reach the employee directly.",
        "Payroll and statutory filing live in the Payroll surface; this module maintains the records they read.",
      ],
    },
    visual: {
      title: "From joining record to controlled access",
      description:
        "Follow one employee from the directory entry to the login, and to the deactivation that ends it.",
      stages: [
        stage(
          "Record created",
          "The employee is added with department, designation and a generated employee code.",
          "The code comes from one per-school counter, so concurrent adds cannot collide.",
          "Employee record and allocated code.",
          "record",
        ),
        stage(
          "Statutory fields validated",
          "PAN, UAN, ESIC and IFSC values are checked as they are stored.",
          "Bank values enter protected storage and display masked from then on.",
          "Validated identifiers and masked finance details.",
          "scope",
        ),
        stage(
          "Access granted",
          "An authorised administrator turns the record into a login and links the two identities.",
          "Granting access is its own recorded action, not a by-product of directory entry.",
          "Linked login, granting actor and handover evidence.",
          "review",
        ),
        stage(
          "In service",
          "The employee uses self-service for their profile, payslips and statutory completion.",
          "Role permissions bound what the login can reach across the workspace.",
          "Self-service activity and the employee's own document shelf.",
          "action",
        ),
        stage(
          "Deactivated",
          "On exit the record is deactivated and its access is withdrawn.",
          "History is preserved rather than deleted, so past payroll and attendance remain provable.",
          "Deactivated record with intact linked history.",
          "evidence",
        ),
      ],
    },
  },

  "transport-management": {
    title: "Routes, Fleet, Riders & Trip Evidence",
    tagline: "Know which bus, which stop, which child.",
    pitch:
      "Run geocoded routes and stops, a fleet and crew master that tracks document expiry, and seat-capacity-aware student allocation with prorated monthly transport fees. Where a compatible location feed is configured, live trips add GPS positions with an honest live-or-stale badge, an append-only boarding log and configured parent alerts.",
    workflows: [
      {
        title: "Geocoded routes and stops",
        description:
          "Define each route's stops with pickup and drop times and a geofence radius, so a route is a real operational object rather than a line in a notice.",
      },
      {
        title: "Fleet master with compliance dates",
        description:
          "Track each vehicle's registration, insurance, permit, pollution certificate and fitness expiry in one place, with alerts before a document lapses.",
      },
      {
        title: "Crew records and licences",
        description:
          "Hold drivers and attendants with their licence validity, and link a crew member to a login only through the explicit link action.",
      },
      {
        title: "Seat-safe allocation",
        description:
          "Allocate a student to a route and stop against the vehicle's seat capacity, with a waitlist when a route is full instead of a silent over-allocation.",
      },
      {
        title: "Prorated transport billing",
        description:
          "Bill transport monthly, prorated from the allocation date and GST-aware, through the same invoice-led fee path as the rest of the school's dues.",
      },
      {
        title: "Live trip positions",
        description:
          "Show a running trip's reported positions where a compatible location feed is configured, with a badge that says plainly whether the feed is live or stale.",
      },
      {
        title: "Append-only boarding log",
        description:
          "Record boarding events as an append-only log, so the question of who boarded which bus is answered from evidence and not from recollection.",
      },
      {
        title: "Configured parent alerts",
        description:
          "Send boarding and trip alerts to allocated riders' guardians through the school's configured channels, subject to consent and opt-out controls.",
      },
      {
        title: "Crew-scoped app access",
        description:
          "Give a driver or attendant only the minimal capture rights their job needs, without opening the student directory or any fee data to them.",
      },
      {
        title: "Emergency signalling",
        description:
          "Raise an SOS from a running trip so the school office sees the vehicle, route and crew involved without hunting for the right phone number.",
      },
      {
        title: "Transport reporting",
        description:
          "Review routes, occupancy, allocations and transport dues together, so a route decision is made against both capacity and collection reality.",
      },
    ],
    automation: {
      label: "Operational controls, honest signals",
      title: "Capacity and expiry are enforced; location is only ever reported.",
      summary:
        "Seat limits, document-expiry alerts and prorated billing are deterministic rules over the school's own records. The live layer reports what the configured feed actually supplies, and says when it is stale.",
      actions: [
        "Block an allocation that would exceed the vehicle's configured seat capacity and offer a waitlist place instead.",
        "Alert on registration, insurance, permit, pollution and fitness expiry before the date passes.",
        "Prorate the monthly transport charge from the allocation date through the standard invoice path.",
        "Label a trip's position feed live or stale rather than showing an old position as current.",
      ],
      boundary:
        "The platform does not drive, dispatch or certify a vehicle. Live tracking depends on a compatible location input the school supplies, boarding evidence is recorded by the crew, and parent alerts are sent only through the school's configured channels with consent and opt-outs respected.",
    },
    uses: [
      "Publish a route with its stops, timings and assigned vehicle and crew.",
      "Allocate a student to a stop and see the seat position that allocation consumes.",
      "Check which vehicle documents expire this month before a trip is scheduled.",
      "Follow a running trip and answer a parent's boarding question from the log.",
      "Reconcile transport dues against the riders actually allocated this month.",
    ],
    roles: [
      "Transport manager",
      "Fleet coordinator",
      "School administrator",
      "Accountant",
      "Driver",
      "Bus attendant",
      "Parent or guardian",
    ],
    metrics: [
      metric(
        "Seat utilisation",
        "Allocated riders against configured seat capacity per route",
        "Shows where a route is over-subscribed or running well below the cost of operating it.",
      ),
      metric(
        "Compliance headroom",
        "Vehicles and crew whose documents expire inside the next review window",
        "Turns statutory expiry into a scheduled task instead of a roadside surprise.",
      ),
      metric(
        "Trip evidence coverage",
        "Trips with a live position feed and boarding entries against trips run",
        "Distinguishes a genuinely tracked operation from one where the feed was never configured.",
      ),
    ],
    availability: {
      status: "Core transport capability · live tracking needs a compatible location input · alerts need configured channels",
      prerequisites: [
        "Routes, stops, vehicles, crew records and student allocations entered before a trip means anything.",
        "A compatible location input for live trips; the platform does not supply tracking hardware.",
        "Configured messaging channels, templates and consent handling for any parent alert.",
        "Transport fee heads configured if monthly transport charges should be billed and collected here.",
        "Crew logins are created through the explicit link action; a crew record alone is not an account.",
      ],
    },
    visual: {
      title: "From route design to boarding evidence",
      description:
        "Follow one child from the allocated stop to the alert their guardian receives.",
      stages: [
        stage(
          "Route published",
          "Stops, timings, geofence radii, vehicle and crew are defined for the route.",
          "Capacity and crew are attached to the route, so later checks have something to enforce.",
          "Route, stop and assignment records.",
          "record",
        ),
        stage(
          "Rider allocated",
          "A student is allocated to a stop against the vehicle's remaining seats.",
          "An over-capacity allocation is refused and offered a waitlist place instead.",
          "Allocation record and current seat position.",
          "scope",
        ),
        stage(
          "Charge raised",
          "The monthly transport charge is prorated from the allocation date and invoiced.",
          "Transport dues use the same invoice-led path as every other fee head.",
          "Transport invoice line with its GST treatment.",
          "action",
        ),
        stage(
          "Trip running",
          "Where a location feed is configured, the trip reports positions with a live-or-stale badge.",
          "The badge never presents a stale position as a current one.",
          "Reported positions and feed freshness.",
          "provider",
        ),
        stage(
          "Boarding recorded",
          "Crew record boarding into an append-only log and configured guardian alerts go out.",
          "The log cannot be quietly rewritten, and alerts respect consent and opt-outs.",
          "Boarding entries and per-channel alert dispatch records.",
          "evidence",
        ),
      ],
    },
  },

  "certificate-generation": {
    title: "Certificates, Serials & Public Verification",
    tagline: "Issued once. Verifiable by anyone.",
    pitch:
      "Issue serialised, QR-verifiable, brand-themed PDF certificates — transfer, bonafide, character, transcript, no-dues, fee-paid, promotion and staff experience letters — each freezing the holder's identity at the moment of issue. A design studio builds custom certificates from merge fields, and any holder's certificate can be checked from its QR without a login.",
    workflows: [
      {
        title: "Typed certificate library",
        description:
          "Start from the certificate types an Indian school actually issues, so a transfer certificate carries transfer fields and a bonafide carries bonafide fields.",
      },
      {
        title: "Merge-field design studio",
        description:
          "Build a custom certificate layout with merge fields that resolve from the source record, instead of maintaining a word-processor file per template.",
      },
      {
        title: "Sequential serial numbers",
        description:
          "Allocate a serial from the school's own register when a certificate is issued, so no two certificates can ever share a serial.",
      },
      {
        title: "Identity frozen at issue",
        description:
          "Snapshot the holder's name and details onto the issued certificate, so a later record edit cannot silently change a document already in someone's hands.",
      },
      {
        title: "Branded PDF output",
        description:
          "Render the certificate as a PDF carrying the school's own logo and accent, matching every other document the school issues.",
      },
      {
        title: "Public QR verification",
        description:
          "Publish a verification page the QR resolves to, so an employer or another school can check a certificate without contacting the office.",
      },
      {
        title: "Revocation that shows",
        description:
          "Revoke an issued certificate so its verification page reports it invalid, rather than leaving a withdrawn document quietly verifiable.",
      },
      {
        title: "No-dues fee gate",
        description:
          "Refuse a no-dues certificate while the student's fees are outstanding, and require an authorised override rather than allowing a silent bypass.",
      },
      {
        title: "Transcripts from real marks",
        description:
          "Fill a transcript's marks table from the student's recorded results, so the document and the gradebook cannot tell different stories.",
      },
      {
        title: "Bulk issue and batch download",
        description:
          "Issue to a whole class at once and download the batch as a single archive, with each certificate still individually serialised.",
      },
      {
        title: "Staff experience letters",
        description:
          "Issue experience and service letters against the employee record, using the same serial register and verification path as student documents.",
      },
    ],
    automation: {
      label: "Issued under school control",
      title: "A certificate is an act of the school, recorded as one.",
      summary:
        "Numbering, identity snapshotting and the fee gate are deterministic controls. Nothing about issuance is a model output, and no certificate is generated without an authorised person asking for it.",
      actions: [
        "Allocate the next serial from one per-school register, safe under concurrent issue.",
        "Freeze the holder's identity onto the issued document at the moment of issue.",
        "Refuse a no-dues certificate while dues remain, unless an authorised person records an override.",
        "Mark a revoked certificate invalid on its public verification page immediately.",
      ],
      boundary:
        "Issuing is a permissioned human action with a recorded issuer. The platform does not attest to facts the school has not recorded, does not sign on the school's behalf, and a verification page reports only what the school issued and whether it is still valid.",
    },
    uses: [
      "Issue a transfer or bonafide certificate for a leaving or continuing student.",
      "Design a custom certificate the school issues that is not in the standard set.",
      "Check whether a presented certificate is genuine and still valid.",
      "Revoke a certificate issued in error and confirm it now verifies as invalid.",
      "Bulk-issue promotion certificates for a class at the end of the academic year.",
    ],
    roles: [
      "Registrar",
      "School administrator",
      "Principal",
      "Authorised certificate issuer",
      "Accountant",
      "Student or employee",
      "External verifier",
    ],
    metrics: [
      metric(
        "Register integrity",
        "Issued certificates against allocated serials",
        "Confirms the register has neither gaps nor duplicates as issuance volume grows.",
      ),
      metric(
        "Verification usage",
        "Public verification checks against certificates issued",
        "Shows whether the QR route is actually replacing calls to the office.",
      ),
      metric(
        "Gate effectiveness",
        "No-dues requests blocked by outstanding fees and overrides recorded",
        "Makes the exception path visible instead of letting a bypass become routine.",
      ),
    ],
    availability: {
      status: "Certificates on a plan that includes it · template and issuer configuration required",
      prerequisites: [
        "A plan that includes Certificates, plus a configured certificate type or designed template.",
        "Authorised issuers identified, since issuance is permissioned and the issuer is recorded.",
        "Accurate source records — a transcript is only as correct as the results behind it.",
        "School branding configured if issued PDFs should carry the school's logo and accent.",
        "The verification page shows only the fields printed on the certificate, never the wider record.",
      ],
    },
    visual: {
      title: "From request to a verifiable document",
      description:
        "Follow one certificate from the gate it must clear to the QR an outsider scans.",
      stages: [
        stage(
          "Request raised",
          "An authorised person requests a certificate against a specific student or employee record.",
          "Only permissioned staff issue, and the requesting actor is recorded.",
          "Request, source record and issuing actor.",
          "scope",
        ),
        stage(
          "Gate checked",
          "Type-specific conditions are evaluated, including outstanding fees for a no-dues certificate.",
          "A blocked case needs a recorded override rather than a quiet bypass.",
          "Gate result and any recorded override.",
          "review",
        ),
        stage(
          "Serial allocated",
          "The next serial is taken from the school's register and bound to this issue.",
          "Allocation is safe under concurrent issue, so serials cannot collide.",
          "Allocated serial and register entry.",
          "record",
        ),
        stage(
          "Document rendered",
          "The branded PDF is produced with the holder's identity frozen as at issue.",
          "A later record edit cannot alter a document already handed over.",
          "Issued PDF with its frozen identity snapshot.",
          "action",
        ),
        stage(
          "Publicly verifiable",
          "The QR resolves to a verification page showing validity and the printed fields.",
          "A revoked certificate reports invalid; no wider record is exposed.",
          "Verification page state and revocation status.",
          "evidence",
        ),
      ],
    },
  },

  "homework-and-assignments": {
    title: "Homework, Submission Records & Follow-up",
    tagline: "What was set, what came back, what is still missing.",
    pitch:
      "Teachers set homework with due dates and maximum marks, then record submission status and grades from an enrolment-backed roster limited to the sections they teach. Student and parent portals show assignments read-only — there is no student self-submission — and a teacher can queue reminders for non-submitters' guardians through the school's configured channels.",
    workflows: [
      {
        title: "Assignments with real deadlines",
        description:
          "Set homework against a class-section and subject with a due date and maximum marks, so the register carries the same information the students were given.",
      },
      {
        title: "Section-scoped rosters",
        description:
          "Work from an enrolment-backed roster limited to the class-sections the teacher actually teaches, rather than a whole-school student list.",
      },
      {
        title: "Recorded submission status",
        description:
          "Mark each student received or not received from that roster, which turns a vague sense of who is behind into a countable register.",
      },
      {
        title: "Graded with clamped marks",
        description:
          "Grade a recorded submission with marks the server clamps to the assignment maximum, so a typo cannot produce an impossible score.",
      },
      {
        title: "Written feedback per student",
        description:
          "Attach feedback to a graded submission so the comment lives with the record instead of only on a returned notebook.",
      },
      {
        title: "AI rubric drafting",
        description:
          "Generate a structured grading rubric of criteria and performance levels whose weights sum to the assignment's total marks, for the teacher to edit.",
      },
      {
        title: "Non-submitter visibility",
        description:
          "See which students have nothing recorded against an assignment, as a working list rather than a calculation someone repeats by hand.",
      },
      {
        title: "Guardian reminders with a cooldown",
        description:
          "Queue a reminder to non-submitters' guardians with a per-assignment cooldown, so the same family is not messaged repeatedly about one task.",
      },
      {
        title: "Read-only family visibility",
        description:
          "Show assignments to students and parents in their portals as reference, without implying an online submission route the product does not provide.",
      },
      {
        title: "Subject-wise workload view",
        description:
          "Review what has been set across a class's subjects, so the homework load on one section can be judged rather than assumed.",
      },
    ],
    automation: {
      label: "Optional AI drafting inside a teacher's workflow",
      title: "Aira can draft a rubric; the teacher grades.",
      summary:
        "Assignment records, submission status and marks are entirely teacher-recorded. Where the assistant tier is enabled, AI drafts a rubric for review — it never marks work and never records a submission.",
      actions: [
        "Draft a criteria-and-levels rubric whose weights sum to the assignment's total marks, for the teacher to edit.",
        "Compile the non-submitter list for an assignment from the recorded register.",
        "Queue a guardian reminder through the configured channel, subject to consent, opt-out and quiet-mode controls.",
        "Hold a repeat reminder within the per-assignment cooldown window.",
      ],
      boundary:
        "AI does not grade, does not decide who submitted, and does not message a family on its own. Submission status and marks are recorded by the teacher for their own sections, and a reminder is a deliberate action that still passes the school's messaging controls.",
    },
    uses: [
      "Set an assignment for a section with its due date and maximum marks.",
      "Record who submitted and grade the work that came in.",
      "Pull the current non-submitter list before a parent conversation.",
      "Queue a guardian reminder for outstanding work.",
      "Review the week's homework load across a class's subjects.",
    ],
    roles: [
      "Subject teacher",
      "Class teacher",
      "Academic coordinator",
      "Vice principal",
      "Student",
      "Parent or guardian",
    ],
    metrics: [
      metric(
        "Submission rate",
        "Submissions recorded received against students on the assignment roster",
        "Reads as an engagement signal per section and subject, not as a judgement about a child.",
      ),
      metric(
        "Grading turnaround",
        "Days between the due date and marks being recorded",
        "Shows where feedback is arriving too late to change anything for the student.",
      ),
      metric(
        "Follow-up load",
        "Reminders queued against non-submitters identified",
        "Keeps outbound pressure on families visible and bounded rather than reflexive.",
      ),
    ],
    availability: {
      status: "Core academic capability · guardian reminders need configured channels · AI drafting on plans that include the assistant",
      prerequisites: [
        "Student rosters, class-sections and teaching assignments configured, since the roster is scope-bound.",
        "A configured messaging provider with consent and opt-out handling for any guardian reminder.",
        "A plan that includes the Aira assistant if rubric drafting should be available.",
        "Student self-submission is not part of this module; portals show assignments read-only.",
        "Plagiarism checking and automated marking are not offered and are not implied anywhere here.",
      ],
    },
    visual: {
      title: "From set work to recorded follow-up",
      description:
        "Follow one assignment from the roster it is set against to the reminder a guardian receives.",
      stages: [
        stage(
          "Assignment set",
          "A teacher creates the assignment against their own class-section and subject.",
          "Record-level scoping keeps a teacher inside the sections they actually teach.",
          "Assignment record with due date and maximum marks.",
          "scope",
        ),
        stage(
          "Roster resolved",
          "The enrolment-backed roster for that section becomes the submission register.",
          "The register is derived from enrolment, not typed by hand.",
          "Roster of students expected to submit.",
          "record",
        ),
        stage(
          "Submissions recorded",
          "The teacher marks each student received or not received.",
          "Status is a teacher decision; nothing infers a submission automatically.",
          "Per-student submission status.",
          "action",
        ),
        stage(
          "Graded and fed back",
          "Recorded submissions are graded with server-clamped marks and written feedback.",
          "Marks cannot exceed the assignment maximum, whatever is typed.",
          "Marks, feedback and any AI-drafted rubric the teacher accepted.",
          "review",
        ),
        stage(
          "Follow-up queued",
          "Guardians of non-submitters receive a reminder through the configured channel.",
          "Consent, opt-out, quiet-mode and the per-assignment cooldown all apply.",
          "Reminder dispatch records with their per-channel outcome.",
          "evidence",
        ),
      ],
    },
  },

  "exam-scheduling": {
    title: "Exam Planning, Datesheets & Marks Control",
    tagline: "A datesheet that cannot double-book a section.",
    pitch:
      "Plan term exams per class, section and subject with maximum marks, pass marks and exam dates, and let the schedule refuse a paper that overlaps another for the same section. Marks entry is section-scoped, an optional second-examiner review can gate a paper, and results flow into report cards and board export layouts.",
    workflows: [
      {
        title: "Term exam structure",
        description:
          "Create the term's papers per class, section and subject with maximum marks, pass marks and the exam date, so the plan is a record rather than a circular.",
      },
      {
        title: "Assessment types",
        description:
          "Tag papers with the assessment type the school's scheme uses, so periodic, term and practical components stay distinguishable in later analysis.",
      },
      {
        title: "Overlap-blocking datesheet",
        description:
          "Refuse a paper that time-overlaps another for the same class-section, with an authorised override for the rare case a school genuinely intends.",
      },
      {
        title: "Section-scoped marks entry",
        description:
          "Limit marks entry to the class-sections a teacher actually teaches, so a paper cannot be marked by someone outside its teaching assignment.",
      },
      {
        title: "Absent and incomplete states",
        description:
          "Record an absent candidate as absent rather than as a zero, which keeps the paper statistics and the later result honest.",
      },
      {
        title: "Per-paper statistics",
        description:
          "See entered, absent, passed, failed and average percentage for a paper at a glance, so a marking anomaly is caught before results are derived.",
      },
      {
        title: "Optional second-examiner review",
        description:
          "Let the entering teacher submit a paper for moderation and a different authorised person verify it, with editing after submission invalidating the review.",
      },
      {
        title: "Reopen with a trail",
        description:
          "Reopen marks entry for a term through an authorised action, so a genuine correction happens on the record instead of beside it.",
      },
      {
        title: "Results derived, not typed",
        description:
          "Derive each student's result from the entered marks and the school's board configuration, rather than from a separately maintained result sheet.",
      },
      {
        title: "Board export layouts",
        description:
          "Export a term's results in the upload layout the board expects, so the school is not re-keying marks into a portal spreadsheet.",
      },
      {
        title: "Class-level AI read",
        description:
          "Where the assistant tier is enabled, get a written read of one paper's class result computed from aggregates only, never from named student data.",
      },
    ],
    automation: {
      label: "Optional AI reading over deterministic marks",
      title: "Every number is calculated; only the explanation is written.",
      summary:
        "Statistics, pass and fail states and derived results come from entered marks and the school's board configuration. Where the assistant is enabled, AI explains a class-level aggregate — it never produces or alters a mark.",
      actions: [
        "Block a paper that time-overlaps another for the same class-section unless an authorised override is recorded.",
        "Compute entered, absent, passed, failed and average figures directly from recorded marks.",
        "Invalidate a submitted or verified moderation state when the underlying marks are edited again.",
        "Explain one paper's class-level result from aggregates, with no student name reaching the model.",
      ],
      boundary:
        "AI never enters, changes or approves a mark. Marks are entered by the teacher assigned to that section, a second examiner is a different person, and an override on a schedule conflict is an authorised, recorded decision rather than a silent allowance.",
    },
    uses: [
      "Lay out a term's papers per class-section with dates and marks.",
      "Publish a datesheet knowing no section has two papers at once.",
      "Enter and check marks for the sections you teach.",
      "Send a paper for second-examiner review before results are derived.",
      "Export the term's results in the board's upload layout.",
    ],
    roles: [
      "Exam coordinator",
      "Examination controller",
      "Academic coordinator",
      "Principal",
      "Subject teacher",
      "Class teacher",
    ],
    metrics: [
      metric(
        "Schedule integrity",
        "Overlapping papers blocked and overrides recorded per datesheet",
        "Shows whether the calendar is being fixed at design time or worked around at the desk.",
      ),
      metric(
        "Marks completeness",
        "Papers with all candidates entered or marked absent against papers held",
        "Tells you exactly which papers will otherwise produce an incomplete result.",
      ),
      metric(
        "Moderation coverage",
        "Papers verified by a second examiner against papers submitted for review",
        "Makes the school's chosen level of marks assurance visible instead of assumed.",
      ),
    ],
    availability: {
      status: "Academic catalogue capability · board configuration required · optional Aira on plans that include it",
      prerequisites: [
        "Classes, sections, subjects, teachers and the term structure configured for the academic year.",
        "Board configuration in place, since pass marks, grading and result derivation read it.",
        "Teaching assignments maintained, because marks entry is limited to the sections a teacher teaches.",
        "A plan that includes the Aira assistant if the class-level written read should be available.",
        "Invigilator rostering, room allocation and seating plans are not part of this module today.",
      ],
    },
    visual: {
      title: "From datesheet to a defensible result",
      description:
        "Follow one paper from the conflict check to the export the board receives.",
      stages: [
        stage(
          "Paper planned",
          "The paper is created for a class-section and subject with its marks and date.",
          "An overlap with another paper for the same section is refused at this point.",
          "Exam record, schedule and any recorded override.",
          "record",
        ),
        stage(
          "Entry scoped",
          "Marks entry opens to the teachers assigned to that class-section.",
          "Record-level scoping keeps the paper inside its teaching assignment.",
          "Entry scope resolved from teaching assignments.",
          "scope",
        ),
        stage(
          "Marks entered",
          "Marks and absences are recorded, and per-paper statistics update from them.",
          "Absent is a distinct state, never a silent zero.",
          "Entered marks, absences and paper statistics.",
          "action",
        ),
        stage(
          "Optionally moderated",
          "The paper is submitted for review and verified by a different authorised person.",
          "Editing marks after submission invalidates the review rather than preserving a stale approval.",
          "Submission, verification and the actors on each side.",
          "review",
        ),
        stage(
          "Result derived",
          "Results derive from entered marks and the school's board configuration, then export.",
          "The export layout follows the board, so nothing is re-keyed into a portal by hand.",
          "Derived results and the board upload file.",
          "evidence",
        ),
      ],
    },
  },

  "migration-copilot": {
    title: "Reviewed Student Data Migration",
    tagline: "See every row before it becomes your school.",
    pitch:
      "Upload a student CSV or XLSX file, review the suggested column map, correct it where the helper guessed wrong, and inspect row-level validation before anything commits. The commit path imports student records only, and imported records stay under onboarding quiet-mode controls while the school verifies the batch.",
    workflows: [
      {
        title: "Upload the file you already have",
        description:
          "Start from the CSV or XLSX export your current system or office spreadsheet produces, rather than reformatting it into a prescribed template first.",
      },
      {
        title: "Suggested column mapping",
        description:
          "Get a proposed mapping from your file's columns to student fields, so the tedious first pass is done and the reviewer's job is correction.",
      },
      {
        title: "Correct the map yourself",
        description:
          "Override any suggested mapping, because the person who knows what a column called 'Grd' means is the school's own administrator.",
      },
      {
        title: "Row-level validation",
        description:
          "See which rows would fail and why, per row and per field, before deciding whether to fix the source file or proceed without them.",
      },
      {
        title: "Preview before commit",
        description:
          "Review exactly what the import would create, so the commit is a decision made against evidence rather than a hopeful button press.",
      },
      {
        title: "Transactional commit",
        description:
          "Commit the reviewed batch through a controlled path that records the run, so an import is a traceable event and not an untracked bulk insert.",
      },
      {
        title: "Import provenance on every record",
        description:
          "Stamp imported students with their import run, so months later you can tell a migrated record from one created in the office.",
      },
      {
        title: "Quiet mode over the batch",
        description:
          "Keep non-essential outbound messaging held while the school verifies the batch, so a legacy dataset cannot trigger a wave of parent messages.",
      },
      {
        title: "Batch rollback",
        description:
          "Undo a batch that turned out wrong instead of unpicking hundreds of records by hand, and re-run the import once the source file is corrected.",
      },
      {
        title: "Numbers assigned by the school's own scheme",
        description:
          "Let imported students take admission numbers from the same allocator every other create path uses, so migration cannot fork the numbering.",
      },
    ],
    automation: {
      label: "AI mapping suggestion, human commit",
      title: "The helper proposes a map; the school commits the data.",
      summary:
        "The AI step is column mapping over the metadata the import workflow supplies. Validation is deterministic, and no record is created until an administrator commits a reviewed batch.",
      actions: [
        "Suggest a column-to-field map for the uploaded file, which the administrator can override entirely.",
        "Validate each row against the student record's own rules and report failures per row and per field.",
        "Hold non-essential outbound messaging for imported records under the school's quiet-mode control.",
        "Record the import run against every created record so a batch can be identified or rolled back.",
      ],
      boundary:
        "Nothing commits automatically. The mapping helper only sees the metadata the import workflow supplies, an administrator reviews the preview and commits, and imported families are not messaged while quiet mode is on. Staff, fee-demand and accounting migration are outside this workflow.",
    },
    uses: [
      "Bring an existing student list into a new workspace before go-live.",
      "Check which rows in a legacy export would fail validation, and why.",
      "Commit a verified batch and confirm what it created.",
      "Roll back a batch that imported against the wrong mapping.",
      "Verify imported records before the school lifts quiet mode and goes live.",
    ],
    roles: [
      "Migration administrator",
      "Admissions administrator",
      "School administrator",
      "Data migration operator",
      "Principal",
      "Implementation lead",
    ],
    metrics: [
      metric(
        "Row readiness",
        "Rows passing validation against rows in the uploaded file",
        "Tells you how much source-data cleanup remains before a commit is worth attempting.",
      ),
      metric(
        "Mapping corrections",
        "Suggested mappings the reviewer changed against columns mapped",
        "Shows how much the file's own vocabulary differed from the platform's, run over run.",
      ),
      metric(
        "Post-import verification",
        "Imported records checked by the school before quiet mode is lifted",
        "Keeps go-live a decision the school makes, rather than a side effect of a successful upload.",
      ),
    ],
    availability: {
      status: "Migration Copilot on a plan that includes it · supported student CSV or XLSX file required",
      prerequisites: [
        "A plan that includes the Migration Copilot, plus a student CSV or XLSX file the workflow supports.",
        "Classes and sections created first, so imported students land in structures that already exist.",
        "An administrator available to review the mapping and preview, since commit is a human action.",
        "Staff, fee-demand and accounting migration are out of scope for this commit path.",
        "Quiet mode stays on until the school explicitly goes live, which is where message holds are lifted.",
      ],
    },
    visual: {
      title: "From a legacy spreadsheet to verified records",
      description:
        "Follow one file from upload to the committed batch the school can still roll back.",
      stages: [
        stage(
          "File uploaded",
          "The school's existing student CSV or XLSX export is uploaded as-is.",
          "The file is staged for review; nothing touches live records at this point.",
          "Uploaded file and its detected columns.",
          "record",
        ),
        stage(
          "Mapping proposed",
          "A column-to-field map is suggested from the workflow's metadata.",
          "The helper sees import metadata only, and every suggestion is overridable.",
          "Proposed map and the reviewer's corrections.",
          "review",
        ),
        stage(
          "Rows validated",
          "Each row is checked against the student record's rules and failures are reported.",
          "Validation is deterministic — the same file produces the same verdict.",
          "Per-row, per-field validation results.",
          "scope",
        ),
        stage(
          "Batch committed",
          "An administrator commits the reviewed batch through the controlled import path.",
          "Records carry their import run, and numbers come from the school's own allocator.",
          "Created records stamped with the import run.",
          "action",
        ),
        stage(
          "Verified under quiet mode",
          "The school checks the batch while non-essential outbound messaging stays held.",
          "A wrong batch can be rolled back; going live is a separate, explicit decision.",
          "Verification state, quiet-mode status and rollback availability.",
          "evidence",
        ),
      ],
    },
  },

  "aira-assistant": {
    title: "Aira — Permission-Scoped School Assistant",
    tagline: "Answers your role is already allowed to see.",
    pitch:
      "Ask supported questions about your school's records by text, and by voice in eleven supported languages where a voice provider is configured. Aira reaches records through a fixed set of permission-checked tools, so each person can only request what their role already permits, and any write action is proposed for a human to confirm before it executes.",
    workflows: [
      {
        title: "Ask in plain language",
        description:
          "Put the question the way you would ask a colleague, instead of learning where a number lives in a reporting screen you use twice a term.",
      },
      {
        title: "A fixed tool surface",
        description:
          "Answer from a defined set of read tools — student counts, fee outstanding, today's attendance, open tasks — rather than an open query over the database.",
      },
      {
        title: "Permission checked per request",
        description:
          "Re-check the asking person's permissions on every request, so a role change takes effect immediately rather than at the next sign-in.",
      },
      {
        title: "Tenant-bounded answers",
        description:
          "Keep every answer inside the asking school's own data, enforced in the database and not only in the application layer.",
      },
      {
        title: "Redaction before the model",
        description:
          "Route model calls through one funnel that pattern-redacts common identifiers such as phone numbers and email addresses before the prompt leaves the platform. Name removal depends on the calling workflow, and permissions and source records stay authoritative.",
      },
      {
        title: "Voice in, answer out",
        description:
          "Speak the question and hear the answer read back in the chosen language, where the school has configured a voice provider.",
      },
      {
        title: "Propose, confirm, execute",
        description:
          "Have Aira propose a supported action and execute it only after an authorised person confirms it, so nothing is changed by a conversation alone.",
      },
      {
        title: "Grounded in the school's own help",
        description:
          "Answer how-to questions from the published help material, so guidance matches the product the school is actually using.",
      },
      {
        title: "Metered as credits",
        description:
          "Meter usage as credits against the school's plan, so AI consumption is visible and bounded rather than an invisible running cost.",
      },
      {
        title: "Health data firewalled",
        description:
          "Exclude health, infirmary, wellbeing and counselling records from the assistant's reach entirely, as special-category data that never enters this surface.",
      },
      {
        title: "Degrades honestly",
        description:
          "Say plainly when a question is outside the supported tools, rather than inventing a confident answer from nothing.",
      },
    ],
    automation: {
      label: "Governed AI over permissioned records",
      title: "Aira can only reach what you can already reach.",
      summary:
        "Aira is bounded by the same permissions, tenancy and record scoping as the rest of the workspace. It reads through fixed tools, and its write path is a proposal a person confirms.",
      actions: [
        "Resolve each question through a fixed set of read tools rather than free-form database access.",
        "Re-check the asking person's permissions on every request, so revoked access applies immediately.",
        "Redact identifiers through one model funnel before any prompt leaves the platform.",
        "Propose a supported write action and execute it only after an authorised person confirms.",
      ],
      boundary:
        "Aira is not an administrator. It cannot exceed the asking person's permissions, cannot reach another school's data, and cannot reach health, infirmary, wellbeing or counselling records at all. Material actions execute only after a human confirmation, and every AI call is metered against the school's credits.",
    },
    uses: [
      "Ask for today's attendance or this month's outstanding fees without opening a report.",
      "Check open tasks or a supported roll-up before a morning meeting.",
      "Ask a how-to question and get an answer grounded in the published help material.",
      "Ask by voice in a supported language and hear the answer read back.",
      "Review and confirm an action Aira has proposed.",
    ],
    roles: [
      "School owner",
      "Principal",
      "School administrator",
      "Accountant",
      "Teacher",
      "Academic coordinator",
    ],
    metrics: [
      metric(
        "Answered scope",
        "Questions resolved through a supported tool against questions asked",
        "Shows where the assistant genuinely helps and where a person still needs the full screen.",
      ),
      metric(
        "Credit consumption",
        "Credits metered per active user per month",
        "Keeps AI cost visible against the plan's allowance instead of accumulating unseen.",
      ),
      metric(
        "Confirmed actions",
        "Proposed actions a person confirmed against actions proposed",
        "Evidences that the human confirmation step is real and being exercised.",
      ),
    ],
    availability: {
      status: "Aira AI assistant on a plan that includes it · credits and per-source permissions apply",
      prerequisites: [
        "A plan that includes the Aira assistant, plus available credits for the school.",
        "Permissions configured per person, since an answer never exceeds what the asker may already see.",
        "A configured voice provider if voice input and read-aloud should be available.",
        "Answers cover the supported tool surface; Aira is not an open query interface over every table.",
        "Health, infirmary, wellbeing and counselling records are excluded from this surface by design.",
      ],
    },
    visual: {
      title: "From question to a scoped, confirmed answer",
      description:
        "Follow one question through the permission, redaction and confirmation gates it must pass.",
      stages: [
        stage(
          "Question asked",
          "A signed-in person asks by text, or by voice where a provider is configured.",
          "The asker's identity and role travel with the request; there is no anonymous mode.",
          "Question, asking identity and channel.",
          "record",
        ),
        stage(
          "Scope resolved",
          "Permissions and tenancy are re-checked for this request.",
          "A revoked permission applies immediately, and another school's data is unreachable.",
          "Resolved permission set and tenant boundary.",
          "scope",
        ),
        stage(
          "Tools consulted",
          "A fixed set of read tools returns the supported records behind the answer.",
          "There is no free-form query path, and excluded record types are never available.",
          "Tool results used to compose the answer.",
          "action",
        ),
        stage(
          "Redacted to the model",
          "The model call goes through one funnel that scrubs identifiers first.",
          "Redaction is default-on for every call, not a per-feature option.",
          "The redacted prompt and the credits metered.",
          "provider",
        ),
        stage(
          "Answered or proposed",
          "Aira answers, or proposes an action for an authorised person to confirm.",
          "Nothing is written by the conversation itself.",
          "Answer text, or the proposal and its confirmation record.",
          "evidence",
        ),
      ],
    },
  },

  "mis-reports": {
    title: "Canonical MIS Reports & Plain-English Routing",
    tagline: "One catalogue. One shape. Real exports.",
    pitch:
      "Thirteen canonical MIS reports — enrolment, attendance, fees, exams, admissions, HR, transport and certificates — each returning a uniform table and KPI shape with date and academic-year parameters. Ask in plain English and the assistant routes your question to the right report and runs its safe parameterised query; the model never writes SQL.",
    workflows: [
      {
        title: "One catalogue, not scattered screens",
        description:
          "Find the school's management reports in one grouped catalogue, so a principal is not hunting the same number through four different module screens.",
      },
      {
        title: "A uniform result shape",
        description:
          "Get every report back as the same table and KPI structure, which makes them comparable, paginated and exportable without per-report special cases.",
      },
      {
        title: "Date and academic-year parameters",
        description:
          "Scope any report to a date range or academic year, so a governing-body question about last term is answered from last term's data.",
      },
      {
        title: "Permission-scoped sources",
        description:
          "Return only the sources the requesting person is permitted to read, so the catalogue does not become a way around module permissions.",
      },
      {
        title: "Plain-English routing",
        description:
          "Ask a question in ordinary language and have it routed to the best-matching report in the fixed catalogue, then run as a safe parameterised query.",
      },
      {
        title: "Narration over aggregates only",
        description:
          "Have the answer explained from a digest with identifying columns removed, so no student or guardian name reaches the model to produce the commentary.",
      },
      {
        title: "Exports staff can actually use",
        description:
          "Download the result for the governing body, the trust office or an auditor, instead of screenshotting a dashboard into a meeting pack.",
      },
      {
        title: "Consistent money treatment",
        description:
          "Read every financial figure from the same integer-paise ledger the fee and accounting surfaces use, so two reports cannot disagree by a rounding step.",
      },
      {
        title: "Pagination that holds up",
        description:
          "Page through a large result rather than truncating it silently, so a wide roll-up remains trustworthy as the school grows.",
      },
      {
        title: "An extensible registry",
        description:
          "Add new reports to the same registry so they inherit the shared parameters, permissions, pagination and export behaviour by construction.",
      },
    ],
    automation: {
      label: "AI routes the question; the platform runs the query",
      title: "The model picks a report — it never writes the query.",
      summary:
        "Natural-language routing maps a question onto one report key from a fixed whitelisted catalogue. The vetted parameterised query then runs tenant-scoped, and only a de-identified aggregate digest is narrated.",
      actions: [
        "Route a plain-English question to one report key from the fixed catalogue.",
        "Run the vetted parameterised query for that report, scoped to the school and the asker's permissions.",
        "Narrate the result from an aggregate digest with identifying columns dropped.",
        "Return the full result, including names, only to the authorised person's own screen.",
      ],
      boundary:
        "The model never writes or executes SQL, never chooses a data source outside the catalogue, and never receives the identifying columns. Report access still follows each source's permissions, so the catalogue cannot be used to read something a role is not entitled to.",
    },
    uses: [
      "Pull the enrolment or attendance position for a governing-body meeting.",
      "Answer a fee-collection question for a specific date range.",
      "Ask a question in plain English when you do not know which report holds the answer.",
      "Export a report for the trust office or an auditor.",
      "Compare a term-on-term figure using the same report with different parameters.",
    ],
    roles: [
      "Principal",
      "School owner",
      "School administrator",
      "Accountant",
      "Academic coordinator",
      "Authorised report analyst",
    ],
    metrics: [
      metric(
        "Catalogue coverage",
        "Management questions answered from a catalogue report against ad-hoc requests to staff",
        "Shows how much routine reporting has moved off individual people's time.",
      ),
      metric(
        "Parameter discipline",
        "Reports run with an explicit date or academic-year scope against total runs",
        "Distinguishes a scoped answer from an all-time number quoted out of context.",
      ),
      metric(
        "Export usage",
        "Exports taken per reporting cycle",
        "Indicates which reports genuinely feed the school's governance pack.",
      ),
    ],
    availability: {
      status: "Reports on a plan that includes it · natural-language routing needs a plan that includes Aira",
      prerequisites: [
        "A plan that includes Reports, plus permissions for each source a report reads.",
        "A plan that includes the Aira assistant if plain-English routing and narration should be available.",
        "Operational data actually recorded in the modules, since a report only reflects what was captured.",
        "The catalogue is a fixed, reviewed set — this is not a free-form SQL or BI query builder.",
        "Custom reports are constrained to the supported student and fee roll-ups documented on their own page.",
      ],
    },
    visual: {
      title: "From a question to an exportable answer",
      description:
        "Follow one management question through routing, permissions and the digest that gets narrated.",
      stages: [
        stage(
          "Question or report chosen",
          "The person picks a report from the catalogue, or asks in plain English.",
          "A plain-English question resolves to one key from the fixed catalogue, never to free SQL.",
          "Selected report key and its parameters.",
          "record",
        ),
        stage(
          "Scope applied",
          "The date or academic-year parameters and the asker's permissions bound the run.",
          "The query is tenant-scoped and permission-checked before it executes.",
          "Applied parameters and resolved permission scope.",
          "scope",
        ),
        stage(
          "Query executed",
          "The vetted parameterised query for that report runs and returns the uniform table and KPIs.",
          "Only reviewed queries execute; the model does not compose one.",
          "Result table, KPIs and pagination state.",
          "action",
        ),
        stage(
          "Digest narrated",
          "An aggregate digest with identifying columns removed is explained in plain English.",
          "Names and contact columns never reach the model that writes the commentary.",
          "The de-identified digest behind the narration.",
          "provider",
        ),
        stage(
          "Exported",
          "The authorised person downloads the full result for their governance pack.",
          "The complete result, including names, goes only to that person's own screen and export.",
          "Export file and the requesting identity.",
          "evidence",
        ),
      ],
    },
  },

  "automated-fee-reminders": {
    title: "Fee Reminders, Cooldowns & Recovery Ladder",
    tagline: "Nudge the right family. Stop the moment they pay.",
    pitch:
      "Run payment nudges across the whole lifecycle — opt-in pre-due reminders at seven, three and zero days, manual and bulk reminders from the invoice list, and an escalating recovery ladder for overdue cases. Every path shares one safety funnel with a paid-guard, so a settled invoice is never chased, and pay links ride along where a gateway is configured.",
    workflows: [
      {
        title: "Opt-in pre-due nudges",
        description:
          "Send a reminder at seven, three and zero days before the due date, each stage exactly once per invoice, and only for schools that switch the schedule on.",
      },
      {
        title: "The paid-guard everywhere",
        description:
          "Check the invoice's current state before any reminder leaves, so a family who paid this morning is not chased this afternoon by a queued message.",
      },
      {
        title: "One outbound-pressure window",
        description:
          "Floor every automated engine with a shared per-invoice pressure window, so the campaign, the ladder and the schedule cannot stack messages on one family.",
      },
      {
        title: "Send-window discipline",
        description:
          "Hold automated sends outside the configured daytime window, so an overdue reminder does not arrive at a family's home late at night.",
      },
      {
        title: "Escalating recovery ladder",
        description:
          "Move an overdue case through reminder, follow-up, firm and final stages, ending in a human call task rather than an endless automated loop.",
      },
      {
        title: "Halt on payment",
        description:
          "Stop the ladder as soon as the invoice settles, and record what was actually recovered rather than crediting the whole outstanding amount to the sequence.",
      },
      {
        title: "Bulk reminders with honest counts",
        description:
          "Select invoices and send in bulk, with the eligible count shown plainly so settled rows are visibly skipped instead of silently included.",
      },
      {
        title: "AI-drafted, human-sent",
        description:
          "Draft a reminder in the family's language, edit it, and send exactly what was reviewed — the draft never dispatches itself.",
      },
      {
        title: "Consent and opt-out respected",
        description:
          "Skip a recipient who has opted out and record that skip as its own outcome, so a suppressed message is never counted as delivered.",
      },
      {
        title: "Quiet mode for new workspaces",
        description:
          "Hold non-essential outbound messaging for a school that has not gone live yet, so importing historic dues cannot trigger a wave of reminders.",
      },
      {
        title: "Pay links that actually work",
        description:
          "Attach an invoice-bound payment link only when a gateway and an absolute address both exist, and otherwise give office-payment instructions instead of a dead link.",
      },
      {
        title: "Honest delivery counting",
        description:
          "Count only provider-accepted sends as sent, and keep skipped, suppressed and failed outcomes visible per channel.",
      },
    ],
    automation: {
      label: "Automated reminders under real safety rails",
      title: "Escalation is automatic; harm prevention is not optional.",
      summary:
        "Scheduling, escalation and drafting can be automated. The paid-guard, pressure window, send window, consent checks and quiet mode apply to every path, including the ones a person triggers.",
      actions: [
        "Send each pre-due stage exactly once per invoice, for schools that enable the schedule.",
        "Halt an active recovery ladder the moment the invoice settles, and record the amount actually recovered.",
        "Defer automated sends outside the configured daytime send window.",
        "Draft the message in a chosen language for a person to review and send verbatim.",
      ],
      boundary:
        "No reminder is sent to a settled invoice, to an opted-out recipient, or from a workspace still in quiet mode. Bulk sending shows its honest eligible count, an AI draft is sent only after a person reviews it, and the ladder's final stage hands the case to a human rather than escalating forever.",
    },
    uses: [
      "Turn on pre-due nudges for the school and confirm the schedule.",
      "Send a single reminder for one invoice from the invoice list.",
      "Run a bulk reminder over selected overdue invoices.",
      "Enrol overdue cases into the recovery ladder and watch it halt on payment.",
      "Review what each channel actually reported after a run.",
    ],
    roles: [
      "Fee-desk executive",
      "Accountant",
      "Communication administrator",
      "School administrator",
      "Principal",
      "Parent or guardian",
    ],
    metrics: [
      metric(
        "Reminder precision",
        "Provider-accepted sends against invoices genuinely eligible at send time",
        "Separates real outreach from messages suppressed by paid, opted-out or quiet-mode checks.",
      ),
      metric(
        "Recovery attribution",
        "Amount actually collected while a ladder was active against the amount outstanding at enrolment",
        "Keeps recovery honest instead of crediting the ladder with the whole balance.",
      ),
      metric(
        "Family message pressure",
        "Messages per invoice per window across all engines",
        "Guards against several engines pressuring one family with the same due amount.",
      ),
    ],
    availability: {
      status: "Issued invoices and configured channels required · the AI recovery ladder needs Automation on your plan",
      prerequisites: [
        "Issued invoices with due dates, since every reminder is bound to an invoice's current state.",
        "A configured messaging channel with approved templates, plus consent and opt-out handling.",
        "A configured payment gateway if reminders should carry a working pay link.",
        "A plan that includes Automation for the escalating AI recovery ladder.",
        "Quiet mode stays on until the school goes live, which deliberately holds non-essential reminders.",
      ],
    },
    visual: {
      title: "From a due date to a halted ladder",
      description:
        "Follow one invoice through the guards every reminder path has to clear.",
      stages: [
        stage(
          "Invoice observed",
          "The engine reads the invoice's current due amount, due date and state.",
          "State is read at send time, never from a stale queue entry.",
          "Invoice state, due amount and due date.",
          "record",
        ),
        stage(
          "Eligibility checked",
          "Paid state, opt-out, quiet mode, pressure window and send window are all evaluated.",
          "Any single guard suppresses the send, and the reason is recorded as its own outcome.",
          "Eligibility verdict and suppression reason.",
          "scope",
        ),
        stage(
          "Message prepared",
          "The stage's message is composed, or drafted in the chosen language for review.",
          "An AI draft is edited and sent by a person; it never dispatches itself.",
          "Prepared message and any pay link attached.",
          "review",
        ),
        stage(
          "Dispatched per channel",
          "The message goes out on the configured channel under its template rules.",
          "Only provider-accepted sends count as sent; skipped and failed stay distinct.",
          "Per-channel dispatch outcome.",
          "provider",
        ),
        stage(
          "Halted on payment",
          "When the invoice settles the ladder halts and the recovered amount is recorded.",
          "The unpaid case ends at a human call task rather than escalating indefinitely.",
          "Halt reason, recovered amount and any created task.",
          "evidence",
        ),
      ],
    },
  },

});

// SEO-6 — curated, NON-templated FAQ answers for the deepened commercial-intent routes. These
// render (and enter the FAQPage schema) IN ADDITION to the four standard templated disclosures.
// Answers are 40–650 chars (the modules-marketing sweep bounds them) and registry-grounded —
// never add a question whose answer needs a claim this file's chapters don't already make.
export const CURATED_MODULE_FAQS = Object.freeze({
  "fee-collection": [
    {
      q: "Does the fee money pass through Vidyapeeth360?",
      a: "No. Online collection runs through the school's own Razorpay or Cashfree account and settles under the school's own gateway agreement. Vidyapeeth360 records provider evidence against the invoice; settlement timing, deductions and final bank credit remain provider and bank evidence for the school to reconcile.",
    },
    {
      q: "What if a parent's checkout window closes mid-payment?",
      a: "A closed window is never treated as proof of payment. An invoice is marked paid only on verified provider evidence, so the record follows the gateway's confirmation — not the browser.",
    },
    {
      q: "Can receipt numbers duplicate?",
      a: "No — receipts are numbered sequentially from an atomic per-school counter, and money is stored as whole paise end-to-end, so there is no rounding drift and no duplicate receipt number.",
    },
  ],
  "result-publishing": [
    {
      q: "Can marks change after results are published?",
      a: "Not silently. Published marks lock, and the sanctioned exception is the re-evaluation workflow: a family requests a re-check of one paper, an authorised re-marker revises that single mark, and the result re-derives with the trail preserved.",
    },
    {
      q: "Do report cards follow our board's grading?",
      a: "Grading is configuration, not code. Bands, pass marks and the marks-versus-grades scale come from your board configuration, so a percentage-only state-board card and a grade-point card both render from the same engine.",
    },
    {
      q: "Can we hold back one student's report card?",
      a: "Yes. Publishing is term-level, and an authorised person can additionally withhold an individual card with a recorded reason; the family's portal does not show it until it is explicitly released.",
    },
  ],
  "student-admission": [
    {
      q: "Who assigns admission numbers?",
      a: "The server does. Admission numbers come from one per-school sequential allocator used by every create path — manual add, approved self-registration, lead conversion and reviewed import — so a school never ends up with two competing numbering schemes.",
    },
    {
      q: "Can families fix a wrong date of birth themselves?",
      a: "They can request it. Students and parents file field-scoped correction requests from their portal; the school reviews and approves, and the record changes through an audited path rather than a phone call.",
    },
  ],
  "student-attendance": [
    {
      q: "Can any teacher mark any class's attendance?",
      a: "No. Record-level scoping limits a classroom teacher to the class-sections they actually teach — resolved from the timetable, explicit teaching assignments, or a homeroom class-teacher link for daily attendance.",
    },
    {
      q: "Will parents get automatic attendance messages?",
      a: "Only what the school configures. The monthly digest and low-attendance scan are enabled per school, respect recorded opt-outs, and a new workspace holds non-essential outbound messages until the school explicitly goes live.",
    },
  ],
  "enquiry-management": [
    {
      q: "Does an enquiry automatically become a student?",
      a: "No. An enquiry moves through the stages your school defines, and admission is a separate, permissioned conversion that creates the student record and assigns the admission number from the school's own allocator.",
    },
    {
      q: "Can AI chase our enquiries on its own?",
      a: "No. Where the assistant tier is enabled, Aira can draft follow-up copy for a person to review and send. Stage changes, admission decisions and outbound messages stay human actions under the school's messaging controls.",
    },
  ],
  "biometric-attendance": [
    {
      q: "Do you supply the biometric hardware?",
      a: "No. This is a provider-agnostic bridge: your school supplies a compatible reader or middleware that can post the supported event shape, and each device gets a tenant-scoped ingest token you can rotate. No vendor SDK is bundled and no hardware is included.",
    },
    {
      q: "Can a device punch overwrite a teacher's attendance mark?",
      a: "Never. A matched IN punch may fill an empty present mark only. An existing teacher mark always wins, OUT punches stay evidence in the punch log, and an unmatched identity goes to a review queue instead of being dropped.",
    },
  ],
  "branch-management": [
    {
      q: "Can a group administrator see every school on the platform?",
      a: "No. Group access is scoped to one legal entity, so an administrator sees only the member schools of their own group. That boundary is enforced on the server for every group request, not by hiding links in the interface.",
    },
    {
      q: "Does each school keep its own branding and configuration?",
      a: "Yes. A member school can override the group's branding, and configuration such as board and grading stays per school. The group layer adds roll-up, consolidated billing and intra-group transfers on top of schools that remain distinct.",
    },
  ],
  "parent-aira": [
    {
      q: "Can a parent ask about another family's child?",
      a: "No. Every parent tool resolves a child strictly within the children linked to that authenticated parent account. There is no path from the parent surface to another family's records, and no school-wide question a parent can ask.",
    },
    {
      q: "Will parents get AI replies on WhatsApp automatically?",
      a: "Only if the school switches it on — it is off by default. When enabled, replies go only to a registered, opted-in parent account, and business-initiated messages still follow the channel's approved-template rules.",
    },
  ],
  "timetable-management": [
    {
      q: "Will the generator publish a timetable by itself?",
      a: "No. Generation produces a draft from the constraints you configured — subject demands, per-teacher caps, room limits. A timetable administrator reviews the conflicts and cap warnings in a dry run and decides what gets published.",
    },
    {
      q: "How are periods covered when a teacher is on leave?",
      a: "Approved leave surfaces the exact periods it leaves open on a substitute board, which ranks available teachers with the same subject first. A coordinator assigns the cover and the assignment is recorded — nothing is swapped automatically.",
    },
  ],
  payroll: [
    {
      q: "Can the person who runs payroll also release the money?",
      a: "No. The person who creates a run cannot mark it paid, and a bank disbursal batch needs a separate authorised approver. Employee bank destinations must be verified before they can receive salary.",
    },
    {
      q: "Does it file our EPF, ESI and TDS returns?",
      a: "It produces the outputs from the locked run — EPFO ECR, the ESIC contribution file, and Form-16 and 24Q worksheets derived from tax actually withheld. Uploading them to the EPFO, ESIC and TRACES portals remains a school action.",
    },
    {
      q: "What if EPF or ESI does not apply to our school?",
      a: "Both are a per-school participation choice. A school outside the Acts can opt out, the run then withholds neither, and the choice is recorded on the run itself so a later toggle cannot retro-change a month you already filed.",
    },
  ],
  "staff-management": [
    {
      q: "Does adding a staff record create a login?",
      a: "No. A directory entry and an app login are separate decisions. An authorised administrator grants access in its own recorded action, which links the employee to that login and hands over a temporary credential the person must change.",
    },
    {
      q: "What happens to a staff member who leaves?",
      a: "They are deactivated, not deleted. Payroll, attendance and document history stay intact and auditable, and their access is withdrawn — so the record remains provable without leaving an active account behind.",
    },
  ],
  "transport-management": [
    {
      q: "Do you provide GPS devices for our buses?",
      a: "No. Live trips display what a compatible location input your school configures actually reports, with a badge that says plainly whether the feed is live or stale. The platform supplies no tracking hardware and never shows a stale position as current.",
    },
    {
      q: "Can more students be allocated than a bus has seats?",
      a: "No. Allocation is checked against the vehicle's configured seat capacity, and a full route offers a waitlist place rather than accepting a silent over-allocation. Monthly transport charges are prorated from the allocation date.",
    },
  ],
  "certificate-generation": [
    {
      q: "Can someone verify a certificate without calling the school?",
      a: "Yes. Each certificate carries a QR that resolves to a public verification page showing whether it is valid and the fields printed on it — nothing wider from the student's record. A revoked certificate verifies as invalid.",
    },
    {
      q: "Can a no-dues certificate be issued while fees are pending?",
      a: "Not silently. Outstanding dues block a no-dues certificate, and proceeding requires an authorised person to record an override — so the exception is visible in the register rather than indistinguishable from a clean issue.",
    },
  ],
  "homework-and-assignments": [
    {
      q: "Can students submit their homework in the app?",
      a: "Not today. Student and parent portals show assignments read-only. Teachers record received or not received from their own section roster and grade what came in — we would rather say that plainly than imply a submission flow that does not exist.",
    },
    {
      q: "Does AI grade the homework?",
      a: "No. Where the assistant tier is enabled, AI can draft a grading rubric whose weights sum to the assignment's total marks, for the teacher to edit. Marks, submission status and feedback are all recorded by the teacher.",
    },
  ],
  "exam-scheduling": [
    {
      q: "Can two papers land on the same section at the same time?",
      a: "The schedule refuses it. A paper that time-overlaps another for the same class-section is blocked, and the rare intentional case needs an authorised override that is recorded rather than silently allowed.",
    },
    {
      q: "Who can enter marks for a paper?",
      a: "Only teachers assigned to that class-section. Where the school uses the optional second-examiner review, a different authorised person verifies the paper — and editing marks after submission invalidates that review instead of preserving a stale approval.",
    },
  ],
  "migration-copilot": [
    {
      q: "What exactly can the Copilot import?",
      a: "Student records from a CSV or XLSX file. Staff, fee-demand and accounting migration are outside this commit path — we list the boundary rather than letting a demo imply a whole-school import.",
    },
    {
      q: "Will importing old data message our parents?",
      a: "No. Imported records carry their import run and stay under quiet mode, which holds non-essential outbound messaging until the school explicitly goes live. A batch that turned out wrong can be rolled back.",
    },
  ],
  "voice-aira": [
    {
      q: "How does a school meeting become a Voice Aira note?",
      a: "An authorised staff member starts a consent-gated live capture, uploads an existing recording, or pastes text. Audio is transcribed with language detection; pasted text goes straight to the reviewable record. In either case, Voice Aira drafts a structured note for a person to review.",
    },
    {
      q: "Does Vidyapeeth360 keep the meeting audio?",
      a: "No. Audio is used to produce the transcript and then discarded. The reviewable transcript and note remain within the school's permission-scoped workspace, so a recording does not become a permanent audio archive.",
    },
    {
      q: "Which languages can Voice Aira understand?",
      a: "Voice Aira supports English plus Hindi, Bengali, Gujarati, Kannada, Malayalam, Marathi, Odia, Punjabi, Tamil and Telugu with automatic language detection. Recognition quality still depends on the recording, accents, background noise and the configured speech provider, so the generated note always remains a draft for human review.",
    },
    {
      q: "What can be shared after the note is reviewed?",
      a: "An authorised person reviews and approves the summary, decisions and action items before anything is shared. Approved summaries and actions can be sent to the intended people; the underlying transcript is never shared with families.",
    },
  ],
  "aira-assistant": [
    {
      q: "Can Aira see more than the person asking?",
      a: "No. Permissions are re-checked on every request and answers stay inside the asking school's own data. Health, infirmary, wellbeing and counselling records are excluded from this surface entirely, whatever the asker's role.",
    },
    {
      q: "Can Aira change something in our school by itself?",
      a: "No. Aira reads through a fixed set of tools, and a supported write action is proposed for an authorised person to confirm before it executes. A conversation on its own never changes a record.",
    },
  ],
  "mis-reports": [
    {
      q: "Does the AI write SQL against our database?",
      a: "No. A plain-English question is routed to one report key from a fixed catalogue, and the vetted parameterised query for that report runs tenant-scoped under the asker's permissions. The model only explains the result.",
    },
    {
      q: "Do student names go to the AI when it explains a report?",
      a: "No. Narration runs over an aggregate digest with identifying columns dropped. The full result, names included, goes only to the authorised person's own screen and export.",
    },
  ],
  "automated-fee-reminders": [
    {
      q: "Could a parent be reminded after they have already paid?",
      a: "No. Every path checks the invoice's current state at send time, and an active recovery ladder halts as soon as the invoice settles. A settled invoice is skipped and the skip is recorded as its own outcome.",
    },
    {
      q: "How do you stop families being messaged too often?",
      a: "One shared per-invoice pressure window floors every automated engine, so the schedule, a campaign and the recovery ladder cannot stack on one family. Automated sends also defer outside the configured daytime window.",
    },
  ],
  announcements: [
    {
      q: "Why does WhatsApp need an approved template?",
      a: "Because the channel requires it: business-initiated WhatsApp outside the service window must use a pre-approved template, so Vidyapeeth360 refuses to send a broadcast as free text. Provider onboarding and template approval remain channel-specific steps for your school.",
    },
    {
      q: "How do we know who actually received an announcement?",
      a: "You review the evidence each configured channel actually returns. In-app can show recorded state, WhatsApp may return delivered or read, SMS carries no read evidence, and email opens appear only when the configured provider supplies them.",
    },
  ],
});

export function curatedModuleFaqsFor(moduleOrKey) {
  const key = typeof moduleOrKey === "string" ? moduleOrKey : moduleOrKey?.key;
  return key ? CURATED_MODULE_FAQS[key] || [] : [];
}

export const DEEP_DIVE_MODULE_KEYS = Object.freeze(Object.keys(DEEP_DIVE_MODULES));

export function deepDiveForModule(moduleOrKey) {
  const key = typeof moduleOrKey === "string" ? moduleOrKey : moduleOrKey?.key;
  return key ? DEEP_DIVE_MODULES[key] || null : null;
}
